import pako from "pako";
import { paramsSerializer } from "../utils/axiosUtils";
import useAxios from "./useAxios";

const useAPI = () => {
  const axiosWithAuth0 = useAxios();
  const doubleEncodeURI = (str) => encodeURIComponent(encodeURIComponent(str));
  const DEFAULT_ENTITY_PAGE_SIZE = 10000;

  const updateEntityExclusion = async (entityId, is_exclusion) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.post(
      `environment/entities/${doubleEncodedEntityId}/is_excluded_from_all`,
      {
        is_exclusion: is_exclusion,
      }
    );
  };

  const updateControlExclusions = async (
    controlID,
    entityIDs,
    exclude,
    reason = null
  ) => {
    return axiosWithAuth0.patch(
      `posture/controls/${controlID}/exclusion-details`,
      {
        entity_ids: entityIDs,
        exclude: exclude,
        reason: reason,
      },
      {}
    );
  };

  const excludeOrIncludeEntityInControls = async (
    entityId,
    controlIDs,
    exclude,
    reason = null
  ) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.patch(
      `environment/entities/${doubleEncodedEntityId}/exclusions`,
      {
        control_ids: controlIDs,
        exclude: exclude,
        reason: reason,
      },
      {}
    );
  };

  const getCustomerFeatureFlagOptions = async () => {
    return axiosWithAuth0.get(`support/customer-feature-flags/options`);
  };

  const getDetectionOptions = async () => {
    return axiosWithAuth0.get(
      `support/customer-feature-flags/detection-options`
    );
  };

  const getControlOptions = async () => {
    return axiosWithAuth0.get(`support/customer-feature-flags/control-options`);
  };

  const getAllCustomersFeatureFlags = async () => {
    return axiosWithAuth0.get(`support/customer-feature-flags/all`);
  };

  const fetchCustomerFeatureFlags = async () => {
    return axiosWithAuth0.get(`customer-feature-flags`);
  };

  const updateCustomerFeatureFlags = async (
    customerIDToUpdate,
    featureFlags
  ) => {
    return axiosWithAuth0.post(`support/customer-feature-flags/set`, {
      customer_to_update: customerIDToUpdate,
      feature_flags: featureFlags,
    });
  };

  const addCustomerFeatureFlag = async (customerIDToUpdate, featureFlag) => {
    return axiosWithAuth0.post(`support/customer-feature-flags/add`, {
      customer_to_update: customerIDToUpdate,
      feature_flags: featureFlag,
    });
  };

  const updateCurrentCustomerFeatureFlag = async (featureFlag) => {
    return axiosWithAuth0.post(
      `support/customer-feature-flags/update`,
      featureFlag
    );
  };

  const addCustomerFeatureFlagsForAllCustomers = async (featureFlag) => {
    return axiosWithAuth0.post(`support/customer-feature-flags/add-for-all`, {
      feature_flags: featureFlag,
    });
  };

  const deleteCustomerFeatureFlag = async (customerID, featureName) => {
    return axiosWithAuth0.delete(`support/customer-feature-flags`, {
      data: {
        customer_id: customerID,
        feature_name: featureName,
      },
    });
  };

  const fetchControlsBatch = async (controlIds) => {
    return axiosWithAuth0.get(`posture/controls/batch`, {
      params: {
        control_ids: controlIds,
      },
      paramsSerializer,
    });
  };

  const saveDashboard = async (dashboardData) => {
    return axiosWithAuth0.post(`custom-dashboard/save`, {
      dashboard_data: dashboardData,
    });
  };

  const loadDashboard = async () => {
    return axiosWithAuth0.get(`custom-dashboard/load`);
  };

  const saveAIDashboard = async (dashboardData) => {
    return axiosWithAuth0.post(`ai-posture/save`, {
      dashboard_data: dashboardData,
    });
  };

  const loadAIDashboard = async () => {
    return axiosWithAuth0.get(`ai-posture/load`);
  };

  const getNHITypesDistributionTrend = async () => {
    return axiosWithAuth0.get(`ai-posture/nhi-types-distribution-trend`);
  };

  const getTopMCPServersTrend = async () => {
    return axiosWithAuth0.get(`ai-posture/top-mcp-servers-trend`);
  };

  const getAIAppUsageTrend = async () => {
    return axiosWithAuth0.get(`ai-posture/ai-app-usage-trend`);
  };

  const getInventoryAssetsTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/inventory-assets-trend`);
  };

  const getToolsPerformanceTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/tools-performance-trend`);
  };

  const getCriticalAssetsTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/critical-assets-trend`);
  };

  const getToolsCoverageTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/tools-coverage-trend`);
  };

  const getToolsRemediationPerformanceTrend = async () => {
    return axiosWithAuth0.get(
      `custom-dashboard/tools-remediation-performance-trend`
    );
  };

  const getTeamRemediationPerformanceTrend = async () => {
    return axiosWithAuth0.get(
      `custom-dashboard/team-remediation-performance-trend`
    );
  };

  const getOpenGapsAgeTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/open-gaps-age-trend`);
  };

  const formatUrl = (url) => {
    url = url.trim();
    url = url.replace(/\/+$/, "");
    if (!url.startsWith("https://")) {
      url = "https://" + url;
    }
    return url;
  };

  const registerJiraIntegration = async (url, email, token) => {
    return axiosWithAuth0.post(`integrations/jira/register`, {
      server_url: formatUrl(url),
      user_email: email,
      api_token: token,
    });
  };

  const registerMondayIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/monday/register`, {
      api_token: api_token,
    });
  };

  const registerAddigyIntegration = async (url, apiToken, orgId = null) => {
    return axiosWithAuth0.post(`integrations/addigy/register`, {
      console_url: formatUrl(url),
      api_token: apiToken,
      org_id: orgId,
    });
  };
  const registerCatoIntegration = async (accountId, apiKey, consoleUrl) => {
    return axiosWithAuth0.post(`integrations/cato/register`, {
      account_id: accountId,
      api_key: apiKey,
      console_url: formatUrl(consoleUrl),
    });
  };
  const registerAsanaIntegration = async (apiToken) => {
    return axiosWithAuth0.post(`integrations/asana/register`, {
      api_token: apiToken,
    });
  };

  const registerSentinelOneIntegration = async (url, api_token) => {
    return axiosWithAuth0.post(`integrations/sentinel_one/register`, {
      url: formatUrl(url),
      api_token: api_token,
    });
  };

  const registerAxurIntegration = async (api_key) => {
    return axiosWithAuth0.post(`integrations/axur/register`, {
      api_key: api_key,
    });
  };

  const registerCircleCIIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/circleci/register`, {
      api_token: api_token,
    });
  };

  const registerClaudeIntegration = async (admin_api_key) => {
    return axiosWithAuth0.post(`integrations/claude/register`, {
      admin_api_key: admin_api_key,
    });
  };

  const registerCursorIntegration = async (admin_api_key) => {
    return axiosWithAuth0.post(`integrations/cursor/register`, {
      admin_api_key: admin_api_key,
    });
  };

  const registerCheckpointHarmonyBrowseIntegration = async (
    clientID,
    clientSecret,
    authenticationURL
  ) => {
    return axiosWithAuth0.post(
      `integrations/checkpoint_harmony_browse/register`,
      {
        client_id: clientID,
        secret_key: clientSecret,
        authentication_url: authenticationURL,
      }
    );
  };

  const registerCheckpointHarmonyEndpointIntegration = async (
    clientID,
    clientSecret,
    authenticationURL
  ) => {
    return axiosWithAuth0.post(
      `integrations/checkpoint_harmony_endpoint/register`,
      {
        client_id: clientID,
        secret_key: clientSecret,
        authentication_url: authenticationURL,
      }
    );
  };

  //TODO:shouldn't be used directly in new code; use IsIntegrated.jsx instead.
  const fetchIntegrations = async (integration = null) => {
    return axiosWithAuth0.get(`integrations`, {}).then((result) => {
      if (integration) {
        return result.data.includes(integration);
      } else {
        return result.data;
      }
    });
  };

  const removeIntegration = async (integration_name) => {
    return axiosWithAuth0.delete(`integrations/${integration_name}/unregister`);
  };

  const fetchGoogleAuthUrl = async () => {
    return axiosWithAuth0.get(`integrations/google/auth-url`);
  };

  const registerGcpIntegration = async () => {
    return axiosWithAuth0.post(`integrations/gcp/register`);
  };

  const fetchGcpConfiguration = async () => {
    return axiosWithAuth0.get(`integrations/gcp/configuration`);
  };

  const revokeGoogleTokens = async (controlId, tokenToUsersMapping) => {
    return axiosWithAuth0.post(`integrations/google/tokens/revoke`, {
      control_id: controlId,
      token_to_users_mapping: tokenToUsersMapping,
    });
  };

  const checkGoogleRemediationPermissions = async () => {
    return axiosWithAuth0.get(
      `integrations/google/remediation-permissions/check`
    );
  };

  const restrictGoogleFileAccess = async (controlId, filesToRestrict) => {
    return axiosWithAuth0.post(`integrations/google/files/restrict`, {
      control_id: controlId,
      files_to_restrict: filesToRestrict,
    });
  };

  const removePersonalEmailPermissionsFromGoogleFiles = async (
    controlId,
    filesToRestrict
  ) => {
    return axiosWithAuth0.post(
      `integrations/google/files/remove-personal-email-permissions`,
      {
        control_id: controlId,
        files_to_restrict: filesToRestrict,
      }
    );
  };

  const getAllAuditLogs = async () => {
    return axiosWithAuth0.get(`posture/remediations/audit-logs`);
  };

  const getRemediationAuditLogs = async (controlId) => {
    return axiosWithAuth0.get(`posture/remediations/remediation-audit-logs`, {
      params: {
        control_id: controlId,
      },
    });
  };

  const autoRemediate = async (controlId, selectedEntitiesMapping) => {
    return axiosWithAuth0.post(`posture/remediations/auto-remediation`, {
      control_id: controlId,
      selected_entities_mapping: selectedEntitiesMapping,
    });
  };

  const reScanAfterAutoRemediation = async (controlId) => {
    return axiosWithAuth0.post(`posture/remediations/re-scan`, controlId);
  };

  const rollbackAutoRemediation = async (controlId) => {
    return axiosWithAuth0.post(`posture/remediations/rollback`, controlId);
  };

  const resetRemediation = async (controlId) => {
    return axiosWithAuth0.post(
      `posture/remediations/reset-remediation`,
      controlId
    );
  };

  const fetchNotionAuthUrl = async () => {
    return axiosWithAuth0.get(`integrations/notion/auth-url`);
  };

  const fetchSlackAuthUrl = async () => {
    return axiosWithAuth0.get(`integrations/slack/auth-url`);
  };

  const fetchSlackSaaSAuthUrl = async () => {
    return axiosWithAuth0.get(`integrations/slack_saas/auth-url`);
  };

  const fetchSalesforceAuthUrl = async (clientID, clientSecret) => {
    return axiosWithAuth0.get(
      `integrations/salesforce/auth-url?client_id=${clientID}&client_secret=${clientSecret}`
    );
  };

  const fetchZoomAuthUrl = async () => {
    return axiosWithAuth0.get(`integrations/zoom/auth-url`);
  };

  const fetchSalesforceCallbackUrl = async () => {
    return axiosWithAuth0.get(`integrations/salesforce/redirect-url`);
  };

  const registerWizIntegration = async (
    client_id,
    client_secret,
    api_endpoint_url
  ) => {
    return axiosWithAuth0.post(`integrations/wiz/register`, {
      client_id: client_id,
      client_secret: client_secret,
      api_endpoint_url: formatUrl(api_endpoint_url),
    });
  };

  const fetchWizProjects = async () => {
    return axiosWithAuth0.get(`integrations/wiz/projects`);
  };

  const getWizConfiguration = async () => {
    return axiosWithAuth0.get(`integrations/wiz/configuration`);
  };

  const updateWizConfiguration = async (selectedProjectIds) => {
    return axiosWithAuth0.post(`integrations/wiz/configure`, {
      selected_project_ids: selectedProjectIds,
    });
  };

  const registerSnykIntegration = async (api_key, datacenter_url) => {
    return axiosWithAuth0.post(`integrations/snyk/register`, {
      api_key: api_key,
      datacenter_url: datacenter_url,
    });
  };

  const registerOpenAIIntegration = async (api_key) => {
    return axiosWithAuth0.post(`integrations/openai/register`, {
      api_key,
    });
  };

  const registerCheckmarxIntegration = async (
    api_key,
    portal_url,
    tenant_name
  ) => {
    return axiosWithAuth0.post(`integrations/checkmarx/register`, {
      api_key: api_key,
      portal_url: formatUrl(portal_url),
      tenant_name: tenant_name,
    });
  };

  const registerCrowdStrikeIntegration = async (
    clientId,
    secret,
    domain = "us-1"
  ) => {
    return axiosWithAuth0.post(`integrations/crowdstrike/register`, {
      client_id: clientId,
      secret: secret,
      domain: domain,
    });
  };

  const checkCrowdStrikeReadScopes = async () => {
    return await axiosWithAuth0.get(
      `integrations/crowdstrike/scopes/check`,
      {}
    );
  };

  const getPlatformStaticIPs = async () => {
    return axiosWithAuth0.get(`/platform-static-ips`, {});
  };

  const getPlatformUrl = async () => {
    return axiosWithAuth0.get(`/platform-url`, {});
  };

  const registerCarbonBlackIntegration = async (
    apiKey,
    apiId,
    orgKey,
    hostname
  ) => {
    return axiosWithAuth0.post(`integrations/carbonblack/register`, {
      api_key: apiKey,
      api_id: apiId,
      org_key: orgKey,
      hostname: hostname,
    });
  };

  const registerQualysIntegration = async (hostname, username, password) => {
    return axiosWithAuth0.post(`integrations/qualys/register`, {
      hostname: hostname,
      username: username,
      password: password,
    });
  };

  const fetchQualysTags = () => axiosWithAuth0.get(`integrations/qualys/tags`);

  const getQualysConfiguration = () =>
    axiosWithAuth0.get(`integrations/qualys/configuration`);

  const updateQualysConfiguration = (data) =>
    axiosWithAuth0.put(`integrations/qualys/configuration`, data);

  const registerBambooHRIntegration = async (subdomain, api_key) => {
    return axiosWithAuth0.post(`integrations/bamboohr/register`, {
      subdomain: subdomain,
      api_key: api_key,
    });
  };

  const generateSnowflakeKMSkey = async () => {
    return axiosWithAuth0.get(`integrations/snowflake/generate-key`, {});
  };

  const getExistingSnowflakeKMSkey = async () => {
    return axiosWithAuth0.get(`integrations/snowflake/key`, {});
  };

  const registerSnowflakeIntegration = async (account_url, username) => {
    return axiosWithAuth0.post(`integrations/snowflake/register`, {
      account_url: account_url,
      username: username,
      kms_key_arn: "",
    });
  };

  const registerJamfIntegration = async (url, credentialsString) => {
    const credentials = JSON.parse(credentialsString);
    const clientId = credentials.client_id;
    const secret = credentials.client_secret;
    return axiosWithAuth0.post(`integrations/jamf/register`, {
      url: formatUrl(url + ".jamfcloud.com"),
      client_id: clientId,
      secret: secret,
    });
  };
  const registerKandjiIntegration = async (url, secret) => {
    return axiosWithAuth0.post(`integrations/kandji/register`, {
      url: url,
      secret: secret,
    });
  };

  const getJamfRolePermissions = async () => {
    return axiosWithAuth0.get(`/integrations/jamf/role_permissions`, {});
  };

  const checkJamfRolePermissions = () => {
    return axiosWithAuth0.get(`/integrations/jamf/role_permissions/check`, {});
  };

  const registerJamfProtectIntegration = async (
    url,
    client_secret,
    client_id
  ) => {
    return axiosWithAuth0.post(`integrations/jamf_protect/register`, {
      url: formatUrl(url + ".protect.jamfcloud.com"),
      client_id: client_id,
      client_secret: client_secret,
    });
  };

  const registerRapid7InsightVMIntegration = async (
    api_key,
    region_code,
    organization_id
  ) => {
    return axiosWithAuth0.post(`integrations/rapid7_insight_vm/register`, {
      api_key: api_key,
      region_code: region_code,
      organization_id: organization_id,
    });
  };

  const registerSecurityScorecardIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/securityscorecard/register`, {
      api_token: api_token,
    });
  };

  const registerIntuneIntegration = async (
    clientSecret,
    clientId,
    tenantId,
    isGovTenant = false
  ) => {
    return axiosWithAuth0.post(`integrations/intune/register`, {
      client_secret: clientSecret,
      client_id: clientId,
      tenant_id: tenantId,
      is_gov_tenant: isGovTenant,
    });
  };

  const registerMicrosoftIntegration = async (
    integrationName,
    registeredMicrosoftIntegrations,
    isGovTenant = false
  ) => {
    const url = `integrations/${integrationName}/register-shared`;
    return axiosWithAuth0.post(url, {
      registered_microsoft_integration: registeredMicrosoftIntegrations[0],
      is_gov_tenant: isGovTenant,
    });
  };

  const getRegisteredMicrosoftIntegrations = async () => {
    return axiosWithAuth0.get(
      `integrations/azure/registered_microsoft_integrations`,
      {}
    );
  };

  const registerMicrosoftDefenderEndpointIntegration = async (
    clientSecret,
    clientId,
    tenantId,
    isGovTenant = false
  ) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_defender_endpoint/register`,
      {
        client_secret: clientSecret,
        client_id: clientId,
        tenant_id: tenantId,
        is_gov_tenant: isGovTenant,
      }
    );
  };

  const getMicrosoftDefenderEndpointPermissions = async () => {
    return axiosWithAuth0.get(
      `/integrations/microsoft_defender_endpoint/permissions`,
      {}
    );
  };

  const registerMicrosoftOffice365Integration = async (
    clientSecret,
    clientId,
    tenantId,
    isGovTenant = false
  ) => {
    return axiosWithAuth0.post(`integrations/microsoft_office_365/register`, {
      client_secret: clientSecret,
      client_id: clientId,
      tenant_id: tenantId,
      kms_key_arn: "",
      thumbprint_b64url: "",
      is_gov_tenant: isGovTenant,
    });
  };

  const getMicrosoftOffice365Permissions = async () => {
    return axiosWithAuth0.get(
      `/integrations/microsoft_office_365/permissions`,
      {}
    );
  };

  const getMicrosoftOffice365ExistingKMSkey = async () => {
    return axiosWithAuth0.get(
      `integrations/microsoft_office_365/existing-kms-key`,
      {}
    );
  };

  const generateMicrosoft365KMSkey = async () => {
    return axiosWithAuth0.get(
      `integrations/microsoft_office_365/generate-key`,
      {}
    );
  };

  const getIntunePermissions = async () => {
    return axiosWithAuth0.get(`/integrations/intune/permissions`, {});
  };

  const getUrlGithubInstall = async () => {
    return axiosWithAuth0.get(`integrations/github/app-url`, {});
  };

  const registerGitHubIntegration = async (installation_id) => {
    return axiosWithAuth0.post(
      `integrations/github/register?installation_id=${installation_id}`,
      {}
    );
  };

  const registerGitLabIntegration = async (groupPageURL, groupToken) => {
    return axiosWithAuth0.post(`integrations/gitlab/register`, {
      group_page_url: groupPageURL,
      group_token: groupToken,
    });
  };

  const registerMicrosoftEntraIDIntegration = async (
    clientSecret,
    clientID,
    tenantID,
    isGovTenant = false
  ) => {
    return axiosWithAuth0.post(`integrations/microsoft_entra_id/register`, {
      client_secret: clientSecret,
      client_id: clientID,
      tenant_id: tenantID,
      is_gov_tenant: isGovTenant,
    });
  };

  const getMicrosoftEntraIDPermissions = async () => {
    return axiosWithAuth0.get(
      `/integrations/microsoft_entra_id/permissions`,
      {}
    );
  };

  const registerMicrosoftAzureCloudIntegration = async (
    clientSecret,
    clientId,
    tenantId,
    isGovTenant = false
  ) => {
    return axiosWithAuth0.post(`integrations/azure/register`, {
      client_secret: clientSecret,
      client_id: clientId,
      tenant_id: tenantId,
      is_gov_tenant: isGovTenant,
    });
  };

  const getMicrosoftAzureCloudPermissions = async () => {
    return axiosWithAuth0.get(`/integrations/azure/permissions`, {});
  };

  const getMicrosoftAzureCloudSubscriptions = async () => {
    return axiosWithAuth0.get(`integrations/azure/subscriptions`, {});
  };

  const registerOrcaIntegration = async (apiToken, regionCode) => {
    return axiosWithAuth0.post(`integrations/orca/register`, {
      api_token: apiToken,
      region_code: regionCode,
    });
  };

  const registerPerceptionPointIntegration = async (apiKey) => {
    return axiosWithAuth0.post(`integrations/perception_point/register`, {
      api_key: apiKey,
    });
  };

  const checkOktaReadScopes = async () => {
    return await axiosWithAuth0.get(`integrations/okta/scopes/check`, {});
  };

  const checkAddigyPermissions = async () => {
    return await axiosWithAuth0.get(
      `integrations/addigy/permissions/check`,
      {}
    );
  };

  const getOktaReadScopes = async () => {
    return axiosWithAuth0.get(`/integrations/okta/scopes`, {});
  };

  const getOktaManagementScopes = async () => {
    return axiosWithAuth0.get(`/integrations/okta/management-scopes`, {});
  };

  const checkOktaManagementScopes = async () => {
    return axiosWithAuth0.get(`/integrations/okta/management-scopes/check`, {});
  };

  const disablePasswordRevealInOkta = async (
    controlId,
    appIds,
    affectedControls
  ) => {
    return axiosWithAuth0.post(`/integrations/okta/disable-password-reveal`, {
      control_id: controlId,
      app_ids: appIds,
      affected_controls: affectedControls,
    });
  };

  const registerTenableIntegration = async (access_key, secret_key) => {
    return axiosWithAuth0.post(`integrations/tenable/register`, {
      access_key: access_key,
      secret_key: secret_key,
    });
  };

  const registerAWSIntegration = async (
    roleARN,
    region,
    nickname,
    isMaster,
    externalId
  ) => {
    const awsRegions = [region];
    const accountId = roleARN.split(":")[4];
    return axiosWithAuth0.post(`integrations/aws/register`, {
      role_ARN: roleARN,
      regions: awsRegions,
      account_id: accountId,
      nickname: nickname,
      is_master: isMaster,
      external_id: externalId,
    });
  };

  const getCloudFormationURL = async () => {
    return axiosWithAuth0.get(`integrations/aws/cloudformation-url`, {});
  };

  const getCloudFormationFolder = async () => {
    return axiosWithAuth0.get(`integrations/aws/cloudformation-folder`, {});
  };

  const registerAWSMembersAccounts = async (roleName) => {
    return axiosWithAuth0.post(
      `integrations/aws/register-members?role_name=${roleName}`,
      {}
    );
  };

  const registerPaloAltoPrismaAccessBrowserIntegration = async (
    clientID,
    clientSecret
  ) => {
    return axiosWithAuth0.post(
      `integrations/paloalto_prisma_access_browser/register`,
      {
        client_id: clientID,
        client_secret: clientSecret,
      }
    );
  };

  const registerOktaIntegration = async (
    customer_domain,
    client_id,
    oauth_app_kid,
    oauth_app_private_key_pem
  ) => {
    return axiosWithAuth0.post(`integrations/okta/register`, {
      customer_domain: formatUrl(customer_domain),
      client_id: client_id,
      oauth_app_kid: oauth_app_kid,
      oauth_app_private_key_pem: oauth_app_private_key_pem,
    });
  };

  const registerTalonIntegration = async (apiKey) => {
    return axiosWithAuth0.post(`integrations/paloalto_talon/register`, {
      api_key: apiKey,
    });
  };

  const checkAWSRootAccountIsIntegrated = async () => {
    return axiosWithAuth0.get(`integrations/aws/root-is-integrated`, {});
  };

  const checkAWSMemberAccountAreIntegrated = async () => {
    return axiosWithAuth0.get(`integrations/aws/members-are-integrated`, {});
  };

  const updateAWSAccount = async (
    roleArn,
    regions,
    nickname,
    accountId,
    organizationId,
    isMaster
  ) => {
    return axiosWithAuth0.post(`integrations/aws/update-account`, {
      role_ARN: roleArn,
      regions: regions,
      nickname: nickname,
      account_id: accountId,
      organization_id: organizationId,
      is_master: isMaster,
    });
  };

  const getAWSUserPermissions = async () => {
    return axiosWithAuth0.get(`/integrations/aws/permissions`, {});
  };

  const registerCloudflareIntegration = async (api_key) => {
    return axiosWithAuth0.post(`integrations/cloudflare/register`, {
      api_key: api_key,
    });
  };
  const registerImpervaWAFIntegration = async (
    api_id,
    api_key,
    console_url
  ) => {
    return axiosWithAuth0.post(`integrations/imperva_waf/register`, {
      api_id: api_id,
      api_key: api_key,
      console_url: console_url,
    });
  };
  const registerDigIntegration = async (api_token, company_id, region) => {
    return axiosWithAuth0.post(`integrations/dig/register`, {
      api_token: api_token,
      company_id: company_id,
      region: region,
    });
  };

  const registerSentraIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/sentra/register`, {
      api_token: api_token,
    });
  };

  const registerOxIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/ox/register`, {
      api_token: api_token,
    });
  };

  const registerBitwardenIntegration = async (
    client_id,
    client_secret,
    region
  ) => {
    return axiosWithAuth0.post(`integrations/bitwarden/register`, {
      client_id: client_id,
      client_secret: client_secret,
      region: region,
    });
  };

  const registerBoxIntegration = async (
    enterprise_id,
    client_id,
    client_secret
  ) => {
    return axiosWithAuth0.post(`integrations/box/register`, {
      enterprise_id: enterprise_id,
      client_id: client_id,
      client_secret: client_secret,
    });
  };

  const registerZscalerZiaIntegration = async (
    username,
    password,
    api_key,
    baseUrl
  ) => {
    return axiosWithAuth0.post(`integrations/zscaler_zia/register`, {
      username: username,
      password: password,
      api_key: api_key,
      base_url: formatUrl(baseUrl),
    });
  };

  const registerPaloAltoCortexXDRIntegration = async (
    api_key,
    api_url,
    key_id
  ) => {
    return axiosWithAuth0.post(`integrations/paloalto_cortex_xdr/register`, {
      api_key: api_key,
      api_url: formatUrl(api_url),
      key_id: key_id,
    });
  };

  const registerPaloAltoCortexXSIAMIntegration = async (
    username,
    password,
    webhook_url
  ) => {
    return axiosWithAuth0.post(`integrations/paloalto_cortex_xsiam/register`, {
      username: username,
      password: password,
      webhook_url: formatUrl(webhook_url),
    });
  };

  const uploadPaloAltoNGFWFile = async (formData) => {
    return axiosWithAuth0.post(
      `integrations/paloalto_ngfw/upload_file`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  const registerPaloAltoNGFWIntegration = async (connection) => {
    return axiosWithAuth0.post(
      `integrations/paloalto_ngfw/register`,
      connection
    );
  };

  const startMicrosoftActiveDirectoryFileUpload = async ({
    target_file,
    file_name,
    file_format,
    content_type,
    file_size,
  }) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_active_directory/start-upload`,
      {
        target_file: target_file,
        file_name: file_name,
        file_format: file_format,
        content_type: content_type,
        file_size: file_size,
      }
    );
  };

  const completeMicrosoftActiveDirectoryFileUpload = async ({
    target_file,
    key,
    upload_id,
    etags,
  }) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_active_directory/complete-upload`,
      {
        target_file: target_file,
        key: key,
        upload_id: upload_id,
        etags: etags,
      }
    );
  };

  const registerMicrosoftActiveDirectoryOfflineIntegration = async () => {
    return axiosWithAuth0.post(
      `integrations/microsoft_active_directory/register-offline`,
      {}
    );
  };

  const registerMicrosoftActiveDirectoryOnPremIntegration = async (
    DomainControllerPrimaryIp,
    DomainControllerSecondaryIp,
    adDomainName,
    username,
    password
  ) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_active_directory/register-onprem`,
      {
        domain_controller_primary_ip: DomainControllerPrimaryIp,
        domain_controller_secondary_ip: DomainControllerSecondaryIp,
        ad_domain_name: adDomainName,
        username: username,
        password: password,
      }
    );
  };

  const getMicrosoftActiveDirectoryStatus = async () => {
    return axiosWithAuth0.get(`integrations/microsoft_active_directory/status`);
  };

  const registerAkamaiWAFIntegration = async (credentials) => {
    return axiosWithAuth0.post(`integrations/akamai_waf/register`, credentials);
  };

  const registerAtlassianIntegration = async (credentials) => {
    return axiosWithAuth0.post(`integrations/atlassian/register`, {
      api_key: credentials.api_key,
      org_id: credentials.org_id,
    });
  };

  const registerCyberArkIdentityIntegration = async (credentials) => {
    return axiosWithAuth0.post(`integrations/cyberark_identity/register`, {
      issuer: credentials.issuer,
      application_name: credentials.application_name,
      scope: credentials.scope,
      client_id: credentials.client_id,
      client_secret: credentials.client_secret,
      org_id: credentials.org_id,
    });
  };

  const registerPanoraysIntegration = async (api_token) => {
    return axiosWithAuth0.post(`integrations/panorays/register`, {
      api_token: api_token,
    });
  };

  const registerZscalerZpaIntegration = async (
    client_id,
    client_secret,
    zpa_customer_id
  ) => {
    return axiosWithAuth0.post(`integrations/zscaler_zpa/register`, {
      client_id: client_id,
      client_secret: client_secret,
      zpa_customer_id: zpa_customer_id,
    });
  };

  const registerZscalerZccIntegration = async (client_id, client_secret) => {
    return axiosWithAuth0.post(`integrations/zscaler_zcc/register`, {
      client_id: client_id,
      client_secret: client_secret,
    });
  };

  const registerTwingateIntegration = async (subdomain, apiToken) => {
    return axiosWithAuth0.post(`integrations/twingate/register`, {
      subdomain: formatUrl(subdomain),
      api_token: apiToken,
    });
  };

  const registerMongoAtlasIntegration = async (public_key, private_key) => {
    return axiosWithAuth0.post(`integrations/mongoatlas/register`, {
      public_key: public_key,
      private_key: private_key,
    });
  };

  const registerNetskopeIntegration = async (api_token, tenant_url) => {
    return axiosWithAuth0.post(`integrations/netskope/register`, {
      api_token: api_token,
      tenant_url: formatUrl(tenant_url),
    });
  };

  const registerHiBobIntegration = async (service_user_id, token) => {
    return axiosWithAuth0.post(`integrations/hibob/register`, {
      service_user_id: service_user_id,
      token: token,
    });
  };

  const registerStarburstIntegration = async (
    client_id,
    client_secret,
    account_url
  ) => {
    return axiosWithAuth0.post(`integrations/starburst/register`, {
      client_id: client_id,
      client_secret: client_secret,
      account_url: formatUrl(account_url),
    });
  };

  const registerGreenhouseIntegration = async (api_key) => {
    return axiosWithAuth0.post(`integrations/greenhouse/register`, {
      api_key: api_key,
    });
  };

  const registerJumpCloudIntegration = async (apiKey) => {
    return axiosWithAuth0.post(`integrations/jumpcloud/register`, {
      api_key: apiKey,
    });
  };

  const registerNinjaOneIntegration = async (
    accessKeyID,
    secretAccessKey,
    consoleUrl
  ) => {
    return axiosWithAuth0.post(`integrations/ninjaone/register`, {
      access_key_id: accessKeyID,
      secret_access_key: secretAccessKey,
      console_url: consoleUrl,
    });
  };

  const registerLayerXIntegration = async (apiToken) => {
    return axiosWithAuth0.post(`integrations/layerx/register`, {
      api_token: apiToken,
    });
  };

  const getHiBobPermissions = async () => {
    return axiosWithAuth0.get(`/integrations/hibob/permissions`, {});
  };

  const getHiBobLifeCycleStatus = async () => {
    return axiosWithAuth0.get(`/integrations/hibob/lifecycle_status`, {});
  };

  const fetchAllAWSAccounts = async () => {
    return axiosWithAuth0.get(`integrations/aws/get-accounts`, {});
  };

  const fetchProjects = async () => {
    return axiosWithAuth0.get(`integrations/jira/read-projects`, {});
  };

  const fetchJiraIssues = async () => {
    return axiosWithAuth0.get(`integrations/jira/read-issues`);
  };

  const fetchJiraIssueTypes = async (projectKey) => {
    return axiosWithAuth0.get(
      `integrations/jira/read-issue-types?project_key=${projectKey}`
    );
  };
  const fetchJSMRequestTypes = async (projectKey) => {
    return axiosWithAuth0.get(
      `integrations/jira/read-jsm-request-types?project_key=${projectKey}`
    );
  };

  const fetchJiraIssueCustomFields = async (projectKey, issueTypeID) => {
    return axiosWithAuth0.get(
      `integrations/jira/read-issue-custom-fields?project_key=${projectKey}&issue_type_id=${issueTypeID}`
    );
  };

  const fetchJSMRequestFields = async (projectKey, requestTypeID) => {
    return axiosWithAuth0.get(
      `integrations/jira/read-jsm-request-fields?project_key=${projectKey}&request_type_id=${requestTypeID}`
    );
  };

  const fetchJSMReporterOptions = async () => {
    return axiosWithAuth0.get(`integrations/jira/read-jsm-reporter-options`);
  };

  const checkJSMIsAgent = async () => {
    return axiosWithAuth0.get(`integrations/jira/jsm/is-agent`);
  };

  const fetchJiraUsers = async (projectKey) => {
    return axiosWithAuth0.get(
      `integrations/jira/read-users?project_key=${projectKey}`
    );
  };

  const fetchJiraLabels = async () => {
    return axiosWithAuth0.get(`integrations/jira/read-labels`, {});
  };

  const createJiraIssue = async (issueContent, linkToGap = "") => {
    return axiosWithAuth0.post(`integrations/jira/create-issue`, {
      ...issueContent,
      link_to_gap: linkToGap,
    });
  };

  const createJiraIssuePerMisconfiguration = async (
    issueContent,
    linkToGap = ""
  ) => {
    return axiosWithAuth0.post(
      `integrations/jira/create-issue/per-misconfiguration`,
      {
        ...issueContent,
        link_to_gap: linkToGap,
      }
    );
  };

  const createJSMIncident = async (issueContent, linkToGap = "") => {
    return axiosWithAuth0.post(`integrations/jira/jsm/create-incident`, {
      ...issueContent,
      link_to_gap: linkToGap,
    });
  };

  const createJSMIncidentPerMisconfiguration = async (
    issueContent,
    linkToGap = ""
  ) => {
    return axiosWithAuth0.post(
      `integrations/jira/jsm/create-incident/per-misconfiguration`,
      {
        ...issueContent,
        link_to_gap: linkToGap,
      }
    );
  };

  const editJiraIssue = async (controlId, newFields) => {
    return axiosWithAuth0.post(`tickets/edit/${controlId}`, { ...newFields });
  };

  const fetchJiraIssueConfig = async () => {
    return axiosWithAuth0.get(`integrations/jira/issue-config`);
  };

  const fetchJiraDefaultIssueConfig = async () => {
    return axiosWithAuth0.get(`integrations/jira/default-issue-config`);
  };

  const linkExistingJiraIssue = async (
    issueLink,
    gapId = null,
    misconfigurationId = null,
    vulnerabilityId = null
  ) => {
    return axiosWithAuth0.post(`integrations/jira/link-existing-issue`, {
      issue_link: issueLink,
      gap_id: gapId,
      misconfiguration_id: misconfigurationId,
      vulnerability_id: vulnerabilityId,
    });
  };

  const updateJiraIssueConfig = async ({
    project,
    issueType,
    assignee,
    labels,
    customFields,
    involvedEntities,
  }) => {
    return axiosWithAuth0.post(`integrations/jira/issue-config`, {
      project,
      issue_type: issueType,
      assignee,
      labels,
      custom_fields: customFields,
      involved_entities: involvedEntities,
    });
  };

  const deleteJiraIssueConfig = async () => {
    return axiosWithAuth0.delete(`integrations/jira/issue-config`);
  };

  const updateJiraIssueDefaultConfig = async ({ labels }) => {
    return axiosWithAuth0.post(`integrations/jira/default-issue-config`, {
      labels,
    });
  };

  const deleteJiraDefaultIssueConfig = async () => {
    return axiosWithAuth0.delete(`integrations/jira/default-issue-config`);
  };

  const fetchJiraIsJSM = async () => {
    return axiosWithAuth0.get(`integrations/jira/jsm`);
  };

  const enableJiraJSM = async () => {
    return axiosWithAuth0.post(`integrations/jira/jsm/enable`);
  };

  const disableJiraJSM = async () => {
    return axiosWithAuth0.post(`integrations/jira/jsm/disable`);
  };

  const fetchAWSAccountID = async () => {
    return axiosWithAuth0.get(`integrations/aws/account-id`, {});
  };

  const generateAWSExternalId = async () => {
    return axiosWithAuth0.get(`integrations/aws/generate-external-id`, {});
  };

  const getSlackChannelInfo = async (channelId) => {
    return axiosWithAuth0.get(`integrations/slack/channels/${channelId}`);
  };

  const sendSlackChannelTestMessage = async (channelId) => {
    return axiosWithAuth0.post(`integrations/slack/test_message`, channelId);
  };

  const removeSlackChannel = async (channelId) => {
    return axiosWithAuth0.delete(`integrations/slack/channels/${channelId}`);
  };

  const requestFullScan = async () => {
    return axiosWithAuth0.get(`scan/full`);
  };

  const requestPartialScan = async (gapIdsToScan, buildRelatedData = true) => {
    const gapsIdsParam = Array.isArray(gapIdsToScan)
      ? gapIdsToScan.join(",")
      : gapIdsToScan;
    return axiosWithAuth0.get(`scan/partial`, {
      params: { gaps_ids: gapsIdsParam, build_related_data: buildRelatedData },
    });
  };

  const requestPartialScanByTools = async (
    selectedTools,
    buildRelatedData = true
  ) => {
    const toolsParam = Array.isArray(selectedTools)
      ? selectedTools.join(",")
      : selectedTools;
    return axiosWithAuth0.get(`scan/partial`, {
      params: { tools: toolsParam, build_related_data: buildRelatedData },
    });
  };

  const requestPostureScan = async (buildRelatedData = true) => {
    return axiosWithAuth0.get(`support/scan/posture`, {
      params: { build_related_data: buildRelatedData },
    });
  };

  const createHeatmap = async () => {
    return axiosWithAuth0.post(`support/scan/heatmap/create`);
  };

  const getScanStatus = async (task_id) => {
    return axiosWithAuth0.get(`scan/task_status/${task_id}`);
  };

  const getIsScanning = async () => {
    return axiosWithAuth0.get(`scan/is_scanning`);
  };

  const runNightlyFlow = async () => {
    return axiosWithAuth0.get(`support/scan/nightly-flow`);
  };

  const runNightlyFlowUI = async () => {
    return axiosWithAuth0.get(`scan/nightly-flow-ui`);
  };

  const getLastSuccessfulScan = async () => {
    return axiosWithAuth0.get(`scan/last-successful-scan`);
  };

  const getScanHealthChecks = async () => {
    return axiosWithAuth0.get(`scan-health-check`);
  };

  const fetchAllScenarioSets = async () => {
    return axiosWithAuth0.get(`posture/scenarios/sets`);
  };

  const setScenarioSetModalWasOpened = async ({ scenarioSetId }) => {
    return axiosWithAuth0.post(`posture/scenarios/sets/user-opened`, {
      scenario_set_id: scenarioSetId,
    });
  };

  const fetchFocusedScenarios = async (scenarioSetId) => {
    return axiosWithAuth0.get(
      `posture/scenarios/sets/focused/${scenarioSetId}`
    );
  };
  const fetchSingleScenariosByScenarioSet = async (scenarioSetId) => {
    return axiosWithAuth0.get(
      `posture/scenarios/sets/single-scenarios/${scenarioSetId}`
    );
  };
  const fetchStepsByScenarioSet = async (scenarioSetId) => {
    return axiosWithAuth0.get(`posture/scenarios/sets/${scenarioSetId}/steps`);
  };

  const fetchScenario = async (scenarioID) => {
    return axiosWithAuth0.get(`posture/scenarios/single/${scenarioID}`);
  };

  const fetchControl = async (controlID) => {
    return axiosWithAuth0.get(`posture/controls/single/${controlID}`);
  };

  const fetchControlMisconfigurations = async (controlID) => {
    return axiosWithAuth0.get(
      `posture/controls/${controlID}/misconfigurations`
    );
  };

  const fetchAllControls = async () => {
    return axiosWithAuth0.get(`posture/controls`);
  };
  const fetchAllRemediations = async () => {
    return axiosWithAuth0.get(`posture/remediations`);
  };
  const fetchRemediation = async (remediation_id) => {
    return axiosWithAuth0.get(`posture/remediations/single/${remediation_id}`);
  };
  const fetchRemediationsBatch = async (remediationIds) => {
    return axiosWithAuth0.get(`posture/remediations/batch`, {
      params: {
        remediation_ids: remediationIds,
      },
      paramsSerializer,
    });
  };

  const updateRemediationCost = async ({ remediation_id, cost }) => {
    return axiosWithAuth0.patch(
      `posture/remediations/${remediation_id}/update-cost`,
      {
        cost: parseInt(cost),
      },
      {}
    );
  };
  const updateRemediationCustomPriority = async ({
    remediation_id,
    custom_priority,
  }) => {
    return axiosWithAuth0.patch(
      `posture/remediations/${remediation_id}/update-custom-priority`,
      {
        custom_priority: parseInt(custom_priority),
      },
      {}
    );
  };
  const updateControlCustomSeverity = async ({
    control_id,
    custom_severity,
  }) => {
    return axiosWithAuth0.patch(
      `posture/controls/${control_id}/update-custom-severity`,
      {
        custom_severity: parseInt(custom_severity),
      },
      {}
    );
  };

  const getControlActivityLogs = async (controlId) => {
    return axiosWithAuth0.get(`posture/controls/${controlId}/activity-logs`);
  };

  const addCommentToControlActivityLogs = async (controlId, comment) => {
    const isBadComment = /(^|\s)```/.test(comment);
    if (isBadComment) {
      throw new Error("Invalid comment");
    }
    return axiosWithAuth0.post(
      `posture/controls/${controlId}/comments`,
      comment
    );
  };

  const getActivityLogImproved = async () => {
    return axiosWithAuth0.get(`custom-dashboard/activity-log-improved`);
  };

  // TODO - Activity logs V2
  const editCommentContent = async (controlID, logID, newContent) => {
    const encodedLogID = encodeURIComponent(logID);
    return axiosWithAuth0.patch(
      `posture/controls/${controlID}/comments/${encodedLogID}`,
      {
        edited_comment: newContent,
      }
    );
  };

  const updateCommentResolveStatus = async (controlID, logID, value) => {
    const encodedLogID = encodeURIComponent(logID);
    return axiosWithAuth0.patch(
      `posture/controls/${controlID}/comments/${encodedLogID}`,
      {
        resolved: value,
      }
    );
  };

  const deleteComment = async (controlID, logID) => {
    const encodedLogID = encodeURIComponent(logID);
    return axiosWithAuth0.patch(
      `posture/controls/${controlID}/comments/${encodedLogID}`,
      {
        delete: true,
      }
    );
  };

  const fetchStep = async (stepId) => {
    return axiosWithAuth0.get(`posture/steps/single/${stepId}`);
  };

  const fetchStepsBatch = async (stepIds) => {
    return axiosWithAuth0.get(`posture/steps/batch`, {
      params: {
        step_ids: stepIds,
      },
      paramsSerializer,
    });
  };

  const fetchAllSteps = async () => {
    return axiosWithAuth0.get(`posture/steps`, {});
  };

  const downloadPlaybook = async (controlId) => {
    return axiosWithAuth0.get(`posture/controls/playbook/${controlId}`, {
      responseType: "blob",
    });
  };

  const fetchRegularAssets = async () => {
    return axiosWithAuth0.get(`assets`, {});
  };

  const fetchSmartAssets = async () => {
    return axiosWithAuth0.get(`assets/smart`, {});
  };

  const fetchAllAssets = async () => {
    return axiosWithAuth0.get(`assets/all`, {});
  };

  const updateSmartAsset = async (assetData) => {
    return axiosWithAuth0.put(
      `assets/smart/${assetData.asset_id}/is_active`,
      {
        asset_id: assetData.asset_id,
        is_active: assetData.is_active,
      },
      {}
    );
  };

  const getAssetsByName = async (assetName) => {
    return axiosWithAuth0.get(`assets`, {
      params: {
        encoded_asset_name: assetName,
      },
    });
  };

  const getEntitiesByAssetID = async (assetId) => {
    return axiosWithAuth0.get(`/assets/${assetId}/entities`, {});
  };

  const getCreationAssetSuggestions = async () => {
    return axiosWithAuth0.get(`assets/suggestion/creation`, {});
  };

  const getUpdateAssetSuggestions = async () => {
    return axiosWithAuth0.get(`assets/suggestion/update`, {});
  };

  // We must pass a unique request_id here ti set the cache key in the BE
  const fetchEntitiesBatch = async (entityIds, request_id) => {
    const compressed = pako.deflate(JSON.stringify(entityIds));
    // We need to encode due to JSON can't handle raw binary data (Uint8Array)
    const encodedAndCompressedEntityIds = btoa(
      String.fromCharCode(...Array.from(compressed))
    );

    return axiosWithAuth0.post(
      `environment/entities/batch`,
      {
        encoded_and_compressed_entity_ids: encodedAndCompressedEntityIds,
      },
      {
        params: { request_id },
      }
    );
  };

  const fetchVulnerabilities = async () => {
    return axiosWithAuth0.get(`environment/vulnerabilities`, {});
  };

  const fetchVulnerabilitiesBatch = async (vulnerabilityIds) => {
    return axiosWithAuth0.get(`environment/vulnerabilities/batch`, {
      params: {
        vulnerability_ids: vulnerabilityIds,
      },
      paramsSerializer,
    });
  };

  const exportVulnerabilitiesAssociations = async (
    vulnerabilityIds,
    tenantIds,
    vulnerabilityFields,
    entityFields
  ) => {
    return axiosWithAuth0.post(`environment/vulnerabilities/export`, {
      vulnerability_ids: vulnerabilityIds,
      tenant_ids: tenantIds?.length > 0 ? tenantIds : null,
      vulnerability_fields:
        vulnerabilityFields?.length > 0 ? vulnerabilityFields : null,
      entity_fields: entityFields?.length > 0 ? entityFields : null,
    });
  };

  const fetchVulnerabilitiesByEntityId = async (entityId) => {
    return axiosWithAuth0.get(`environment/vulnerabilities`, {
      params: {
        entity_id: entityId,
      },
    });
  };

  const getVulnerabilityFields = async () => {
    return axiosWithAuth0.get(`environment/vulnerabilities/fields`);
  };

  const fetchVulnerabilitiesByEntityType = async (entityType) => {
    return axiosWithAuth0.get(
      `environment/entities/vulnerabilities/${entityType}`,
      {}
    );
  };

  const fetchAssociationsByEntityType = async (entityType) => {
    return axiosWithAuth0.get(
      `environment/entities/associations/${entityType}`,
      {}
    );
  };

  const fetchEntitiesByVulnerability = async (vulnerabilityId) => {
    const doubleEncodedVulnerabilityId = doubleEncodeURI(vulnerabilityId);
    return axiosWithAuth0.get(
      `environment/entities/vulnerabilities/${doubleEncodedVulnerabilityId}/entities`,
      {}
    );
  };

  const updateAssetsContainingTheEntity = async (entityId, assetIDs) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.patch(
      `environment/entities/${doubleEncodedEntityId}/assets`,
      assetIDs,
      {}
    );
  };

  const createAsset = async (assetData) => {
    return axiosWithAuth0.post(
      `assets`,
      {
        name: assetData.name,
        type: assetData.type,
        reason: assetData.reason,
        importance: 1,
        entity_ids: assetData.entity_ids,
        business_value: assetData.businessValue
          ? parseInt(assetData.businessValue)
          : 0,
      },
      {}
    );
  };

  const acceptSuggestion = async (assetData) => {
    return axiosWithAuth0.post(
      `assets/suggestion/accept`,
      {
        asset_suggestion_id: assetData.asset_suggestion_id,
        name: assetData.name,
        type: assetData.type,
        importance: parseInt(assetData.importance),
        entity_ids: assetData.entity_ids,
        reason: assetData.reason,
      },
      {}
    );
  };

  const generateAutoSuggestions = async (entityIds) => {
    return axiosWithAuth0.post(`support/assets/suggestion/generate`, {});
  };

  const generateSmartAsset = async () => {
    return axiosWithAuth0.post(`support/assets/smart/generate`, {});
  };

  const updateAsset = async (assetData) => {
    return axiosWithAuth0.put(
      `assets/${assetData.asset_id}`,
      {
        asset_id: assetData.asset_id,
        name: assetData.name,
        type: assetData.type,
        reason: assetData.reason,
        importance: 1,
        entity_ids: assetData.entity_ids,
        business_value: parseInt(assetData.business_value),
      },
      {}
    );
  };

  const updateAssetReason = async (assetId, reason) => {
    return axiosWithAuth0.put(
      `assets/${assetId}/reason`,
      {
        reason: reason,
      },
      {}
    );
  };

  const deleteAsset = async (assetId) => {
    return axiosWithAuth0.delete(`assets/${assetId}`, {});
  };

  const removeEntityFromAsset = async (entityId, assetId) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.delete(
      `assets/${assetId}/entities/${doubleEncodedEntityId}`,
      {}
    );
  };

  const snoozeControl = async (
    controlIds,
    userEmail,
    userName,
    periodDays,
    snoozeReason
  ) => {
    // Limit the snooze reason to 100 characters
    const snoozeReasonMaxLength = 100;
    const trimmedSnoozeReason =
      snoozeReason.length > snoozeReasonMaxLength
        ? snoozeReason.substring(0, snoozeReasonMaxLength)
        : snoozeReason;

    return axiosWithAuth0.post(
      `posture/controls/snooze`,
      {
        control_ids: controlIds,
        user_email: userEmail,
        user_name: userName,
        period_days: periodDays,
        snooze_reason: trimmedSnoozeReason,
      },
      {}
    );
  };

  const unsnoozeControl = async (controlIds, user) => {
    return axiosWithAuth0.post(
      `posture/controls/unsnooze`,
      {
        control_ids: controlIds,
        user_email: user,
      },
      {}
    );
  };

  const snoozeMisconfiguration = async (
    misconfigurationIds,
    userEmail,
    userName,
    periodDays,
    snoozeReason
  ) => {
    // Limit the snooze reason to 100 characters
    const snoozeReasonMaxLength = 100;
    const trimmedSnoozeReason =
      snoozeReason.length > snoozeReasonMaxLength
        ? snoozeReason.substring(0, snoozeReasonMaxLength)
        : snoozeReason;

    return axiosWithAuth0.post(
      `posture/controls/misconfigurations/snooze`,
      {
        misconfiguration_ids: misconfigurationIds,
        user_email: userEmail,
        user_name: userName,
        period_days: periodDays,
        snooze_reason: trimmedSnoozeReason,
      },
      {}
    );
  };

  const unsnoozeMisconfiguration = async (misconfigurationIds, user) => {
    return axiosWithAuth0.post(
      `posture/controls/misconfigurations/unsnooze`,
      {
        misconfiguration_ids: misconfigurationIds,
        user_email: user,
        // not used but required for API convenience (similar to snooze)
        period_days: 0,
      },
      {}
    );
  };

  const assignControlAndRemediation = async (
    controlRemediationId,
    userEmail
  ) => {
    return axiosWithAuth0.post(`posture/controls/assign`, {
      control_remediation_id: controlRemediationId,
      user_email: userEmail,
    });
  };

  const getTicketByControlID = async (controlId) => {
    return axiosWithAuth0.get(`/tickets`, {
      params: { control_id: controlId },
    });
  };

  const getJiraIssueByControlID = async (controlId) => {
    return axiosWithAuth0.get(`/tickets/jira`, {
      params: { control_id: controlId },
    });
  };

  const getTicketByVulnerabilityId = async (vulnerabilityId) => {
    return axiosWithAuth0.get(`/tickets`, {
      params: { vulnerability_id: vulnerabilityId },
    });
  };

  const getJiraIssueByVulnerabilityId = async (vulnerabilityId) => {
    return axiosWithAuth0.get(`/tickets/jira`, {
      params: { vulnerability_id: vulnerabilityId },
    });
  };

  const getTicketByMisconfigurationId = async (misconfigurationId) => {
    const doubleEncodedMisconfigurationId = doubleEncodeURI(misconfigurationId);
    return axiosWithAuth0.get(
      `/tickets/misconfiguration/${doubleEncodedMisconfigurationId}`,
      {}
    );
  };

  const getAllTickets = async () => {
    return axiosWithAuth0.get(`/tickets/all-control-tickets`, {});
  };

  const getMisconfigurationsWithTickets = async (controlId) => {
    return axiosWithAuth0.get(`/tickets/misconfigurations/${controlId}`, {});
  };

  const isPlaybookAvailable = async (control_id) => {
    return axiosWithAuth0.get(
      `posture/controls/playbook/${control_id}/available`,
      {}
    );
  };

  const deleteAWSAccount = async (accountId) => {
    return axiosWithAuth0.delete(`integrations/aws/account/${accountId}`, {});
  };

  const trackEvent = async (message, parameters = {}) => {
    if (process.env.REACT_APP_SEND_TRACKING_EVENTS === "true") {
      return axiosWithAuth0.post(`track`, {
        message: message,
        parameters: parameters,
      });
    }
  };

  const sendErrorReport = async (userMessage, errorDetails, pageUrl) => {
    return axiosWithAuth0.post(`support/error-report`, {
      user_message: userMessage,
      error_details: errorDetails,
      page_url: pageUrl,
    });
  };

  const fetchEntityById = async (entityId) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.get(
      `environment/entities/entity_id/${doubleEncodedEntityId}`
    );
  };
  const fetchEntitiesByType = async (
    entity_type,
    page = 1,
    page_size = DEFAULT_ENTITY_PAGE_SIZE
  ) => {
    return await axiosWithAuth0.get(
      `environment/entities?entity_type=${entity_type}&page=${page}&page_size=${page_size}`,
      {}
    );
  };

  const fetchAllEntities = async (page = 1, page_size = 10000) => {
    return await axiosWithAuth0.get(
      `environment/entities?page=${page}&page_size=${page_size}`
    );
  };

  const fetchAllEntitiesRds = async (page = 1, page_size = 10000) => {
    return await axiosWithAuth0.post(`postgres/entities/list`, {
      page,
      page_size,
      include_total: false,
    });
  };

  const fetchEntitiesCount = async (entityType = null) => {
    return await axiosWithAuth0.get(
      `environment/entities/count${
        entityType ? `?entity_type=${entityType}` : ""
      }`
    );
  };

  const fetchEntitiesCountRds = async (entityType = null) => {
    return await axiosWithAuth0.get(
      `postgres/entities/count${entityType ? `?entity_type=${entityType}` : ""}`
    );
  };

  const fetchEntitiesByTypeRds = async (
    entity_type,
    page = 1,
    page_size = DEFAULT_ENTITY_PAGE_SIZE
  ) => {
    return await axiosWithAuth0.post(`postgres/entities/list`, {
      entity_type,
      page,
      page_size,
      include_total: false,
    });
  };

  const getEntitiesRds = async (payload) => {
    return await axiosWithAuth0.post(`postgres/entities/list`, payload);
  };

  const fetchEntityByIdRds = async (entityId, customerId) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return await axiosWithAuth0.get(
      `postgres/entities/entity/${doubleEncodedEntityId}`,
      { params: { customer_id: customerId } }
    );
  };

  const fetchRawDataFields = async (fields) => {
    return await axiosWithAuth0.get(`postgres/entities/raw_data`, {
      params: { fields },
      paramsSerializer,
    });
  };

  const fetchEntityRawDataRds = async (customerId, entityId) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return await axiosWithAuth0.get(
      `postgres/entities/raw_data/${doubleEncodedEntityId}?customer_id=${customerId}`
    );
  };

  const getAllComplexMisconfiguredEntitiesForControl = async (control_id) => {
    return await axiosWithAuth0.get(
      `environment/entities/control/misconfigured/${control_id}`,
      {}
    );
  };

  const getEntitiesByControlAndMisconfig = async (
    controlId,
    misconfigurationId
  ) => {
    const encodedMisconfigId = btoa(misconfigurationId); // base64
    return axiosWithAuth0.get(
      `environment/entities/control/misconfigured/${controlId}/${encodedMisconfigId}`
    );
  };

  const fetchEntityInstalledPrograms = async (entityId) => {
    const encodedEntityId = encodeURIComponent(entityId);
    return axiosWithAuth0.get(
      `postgres/entities/entity/${encodedEntityId}/programs`
    );
  };

  const fetchProgramFilterOptions = async () => {
    return axiosWithAuth0.get(`postgres/entities/programs/options`);
  };

  const exportEntities = async (exportRequest) => {
    return axiosWithAuth0.post(`postgres/entities/export`, exportRequest, {
      responseType: "blob",
    });
  };

  const fetchEntityFilterOptions = async (fields, entity_type = null) => {
    return axiosWithAuth0.post(`postgres/entities/filters/options`, {
      fields,
      entity_type,
    });
  };

  const generateInternalDashboardSnapshots = async (date = null) => {
    return axiosWithAuth0.post(
      `support/internal-dashboard/snapshots/generate`,
      {
        date: date,
      }
    );
  };

  const getInternalDashboard = async (date = null, envId = null) => {
    return axiosWithAuth0.get(`support/internal-dashboard`, {
      params: { date, env_id: envId },
    });
  };

  const getInternalDashboardTrends = async (
    fromDate = null,
    toDate = null,
    envId = null
  ) => {
    return axiosWithAuth0.get(`support/internal-dashboard/trends`, {
      params: { from_date: fromDate, to_date: toDate, env_id: envId },
    });
  };

  const saveInternalDashboardLayout = async (layoutData) => {
    return axiosWithAuth0.post(`support/internal-dashboard/save-layout`, {
      layout_data: layoutData,
    });
  };

  const loadInternalDashboardLayout = async () => {
    return axiosWithAuth0.get(`support/internal-dashboard/load-layout`);
  };

  const getTerminatedUsersAndHosts = async () => {
    return await axiosWithAuth0.get(
      `environment/entities/terminated_users_and_hosts`,
      {}
    );
  };

  const runEntityCollection = async (selectedTools, entityTypes) => {
    return axiosWithAuth0.get(`support/scan/entities/collection`, {
      params: {
        tools: selectedTools.join(","),
        entity_types: entityTypes.join(","),
      },
    });
  };

  const getEntitiesOverview = async () => {
    return await axiosWithAuth0.get(`environment/entities/overview`, {});
  };

  const refreshEntitiesOverview = async () => {
    return await axiosWithAuth0.post(`environment/entities/overview`, {});
  };

  const runVulnerabilityCollection = async (selectedTools) => {
    return axiosWithAuth0.get(`support/scan/vulnerabilities/collection`, {
      params: {
        tools: selectedTools.join(","),
      },
    });
  };

  const runBuildGRCUser = async () => {
    return axiosWithAuth0.get(`/support/scan/build-grc-users`, {});
  };

  const clearEntitiesCache = async () => {
    return axiosWithAuth0.patch(`environment/entities/clear-cache`, {});
  };

  const clearSecretsCache = async () => {
    return axiosWithAuth0.patch(`support/cache/clear-secrets-cache`, {});
  };

  const clearRunningTasks = async (customer_id) => {
    const doubleEncodedCustomerId = doubleEncodeURI(customer_id);
    return await axiosWithAuth0.post(
      `support/platform-config/clear-running-tasks?customer_id_to_clear=${doubleEncodedCustomerId}`,
      {}
    );
  };

  const getIntegrationsInfo = async () => {
    return axiosWithAuth0.get(`integrations-info`, {});
  };

  const getIntegrationsMCPIDs = async (tools = []) => {
    if (!tools || tools.length === 0) {
      return { data: {} };
    }

    return axiosWithAuth0.get(`integrations-mcp-ids`, {
      params: { tools },
      paramsSerializer,
    });
  };

  const setMCPIDToIntegration = async (integrationName, mcpID) => {
    return axiosWithAuth0.post(`integrations-mcp-ids`, {
      integration_name: integrationName,
      mcp_id: mcpID,
    });
  };

  const testJamfMCPScriptSearchID = async (mcpScriptSearchID) => {
    return axiosWithAuth0.get(`integrations/jamf/test-mcp-script-search-id`, {
      params: { mcp_script_search_id: mcpScriptSearchID },
    });
  };

  const testIntuneMCPScriptSearchID = async (mcpScriptSearchID) => {
    return axiosWithAuth0.get(`integrations/intune/test-mcp-script-search-id`, {
      params: { mcp_script_search_id: mcpScriptSearchID },
    });
  };

  const testKandjiMCPScriptSearchID = async (mcpScriptSearchID) => {
    return axiosWithAuth0.get(`integrations/kandji/test-mcp-script-search-id`, {
      params: { mcp_script_search_id: mcpScriptSearchID },
    });
  };

  const registerTorqIntegration = async (webhook_url) => {
    return axiosWithAuth0.post(`integrations/torq/register`, {
      webhook_url: formatUrl(webhook_url),
    });
  };

  const registerWorkatoIntegration = async (webhook_url) => {
    return axiosWithAuth0.post(`integrations/workato/register`, {
      webhook_url: formatUrl(webhook_url),
    });
  };

  const registerCoralogixIntegration = async (webhook_url) => {
    return axiosWithAuth0.post(`integrations/coralogix/register`, {
      webhook_url: formatUrl(webhook_url),
    });
  };

  const registerSumoLogicIntegration = async (webhook_url) => {
    return axiosWithAuth0.post(`integrations/sumologic/register`, {
      webhook_url: formatUrl(webhook_url),
    });
  };

  const registerSplunkCloudIntegration = async (
    webhookUrl,
    apiToken,
    tokenIndex,
    port
  ) => {
    return axiosWithAuth0.post(`integrations/splunk_cloud/register`, {
      webhook_url: formatUrl(webhookUrl),
      api_token: apiToken,
      token_index: tokenIndex,
      port: port,
    });
  };

  const getTorqConfig = async () => {
    return axiosWithAuth0.get(`integrations/torq/config`);
  };

  const updateTorqConfig = async (config) => {
    return axiosWithAuth0.post(`integrations/torq/config`, config);
  };

  const getSplunkCloudConfig = async () => {
    return axiosWithAuth0.get(`integrations/splunk_cloud/config`);
  };

  const updateSplunkCloudConfig = async (config) => {
    return axiosWithAuth0.post(`integrations/splunk_cloud/config`, config);
  };

  const getPaloAltoCortexXSIAMConfig = async () => {
    return axiosWithAuth0.get(`integrations/paloalto_cortex_xsiam/config`);
  };

  const updatePaloAltoCortexXSIAMConfig = async (config) => {
    return axiosWithAuth0.post(
      `integrations/paloalto_cortex_xsiam/config`,
      config
    );
  };

  const getCoralogixConfig = async () => {
    return axiosWithAuth0.get(`integrations/coralogix/config`);
  };

  const updateCoralogixConfig = async (config) => {
    return axiosWithAuth0.post(`integrations/coralogix/config`, config);
  };

  const getSumoLogicConfig = async () => {
    return axiosWithAuth0.get(`integrations/sumologic/config`);
  };

  const updateSumoLogicConfig = async (config) => {
    return axiosWithAuth0.post(`integrations/sumologic/config`, config);
  };

  const getWorkatoConfig = async () => {
    return axiosWithAuth0.get(`integrations/workato/config`);
  };

  const updateWorkatoConfig = async (config) => {
    return axiosWithAuth0.post(`integrations/workato/config`, config);
  };

  const toggleFavoriteControl = async (controlId, isFavorite) => {
    return axiosWithAuth0.post(`posture/controls/toggle-favorite`, {
      control_id: controlId,
      is_favorite: isFavorite,
    });
  };

  const getFavoriteControls = async () => {
    return axiosWithAuth0.get(`posture/controls/favorites`);
  };

  const toggleFavoriteEntity = async (entityId, isFavorite) => {
    return axiosWithAuth0.post(`environment/entities/toggle-favorite`, {
      entity_id: entityId,
      is_favorite: isFavorite,
    });
  };

  const getFavoriteEntities = async () => {
    return axiosWithAuth0.get(`environment/entities/favorites`);
  };

  const createLabel = async (label, label_color) => {
    return axiosWithAuth0.post(`environment/entities/labels`, {
      label_name: label,
      label_color: label_color,
    });
  };

  const getAllLabels = async () => {
    return axiosWithAuth0.get(`environment/entities/labels`);
  };

  const updateEntitiesLabelsBatch = async (
    entityIDs,
    labelsToAdd,
    labelsToRemove
  ) => {
    return axiosWithAuth0.post(`environment/entities/labels/batch`, {
      entity_ids: entityIDs,
      labels_to_add: labelsToAdd,
      labels_to_remove: labelsToRemove,
    });
  };

  const deleteLabel = async (labelId) => {
    return axiosWithAuth0.delete(
      `environment/entities/labels/${encodeURIComponent(labelId)}`
    );
  };

  const createTag = async (tagKey, tagValue) => {
    return axiosWithAuth0.post(`environment/entities/tags`, {
      tag_key: tagKey,
      tag_value: tagValue,
    });
  };

  const getAllCustomTags = async () => {
    return axiosWithAuth0.get(`environment/entities/tags`);
  };

  const updateEntitiesTagsBatch = async (
    entityIDs,
    tagsToAdd,
    tagsToRemove
  ) => {
    return axiosWithAuth0.post(`environment/entities/tags/batch`, {
      entity_ids: entityIDs,
      tags_to_add: tagsToAdd,
      tags_to_remove: tagsToRemove,
    });
  };

  const deleteTag = async (tagId) => {
    return axiosWithAuth0.delete(
      `environment/entities/tags/${encodeURIComponent(tagId)}`
    );
  };

  const fetchEnvFeatureFlags = async () => {
    return await axiosWithAuth0.get("feature-flags");
  };

  const updateEnvFeatureFlags = async (featureFlags) => {
    return axiosWithAuth0.post("support/env-feature-flags", {
      feature_flags: featureFlags,
    });
  };

  const addEnvFeatureFlag = async (featureFlag) => {
    return axiosWithAuth0.post("support/env-feature-flags/add", {
      feature_flags: featureFlag,
    });
  };

  const deleteEnvFeatureFlag = async (featureName) => {
    return axiosWithAuth0.delete("support/env-feature-flags", {
      data: {
        feature_name: featureName,
      },
    });
  };

  const fetchPlatformSecret = async () => {
    return await axiosWithAuth0.get("support/platform-config/platform-secret");
  };

  const updatePlatformSecretField = async (fieldName, fieldValue) => {
    return axiosWithAuth0.patch("support/platform-config/platform-secret", {
      field_name: fieldName,
      field_value: fieldValue,
    });
  };

  const checkControlIsViewed = async (control_id) => {
    return await axiosWithAuth0.get(
      `limits/controls/${control_id}/is-viewed`,
      {}
    );
  };

  const getControlViewedToday = async () => {
    return await axiosWithAuth0.get(`limits/controls/viewed-list`, {});
  };

  const markControlAsViewed = async (control_id) => {
    return await axiosWithAuth0.post(
      `limits/controls/${control_id}/viewed`,
      {}
    );
  };

  const refreshLimitsCache = async (customer_id_to_refresh) => {
    const doubleEncodedCustomerId = doubleEncodeURI(customer_id_to_refresh);

    return await axiosWithAuth0.post(
      `support/poc-limits/refresh-cache?customer_id_to_refresh=${doubleEncodedCustomerId}`,
      {}
    );
  };

  const getRemainingControlsLimitToday = async () => {
    return axiosWithAuth0.get(`limits/controls/remaining-today`, {});
  };

  const getRemainingControlsLimit = async () => {
    return axiosWithAuth0.get(`limits/controls/remaining-total`, {});
  };

  const checkControlIsAllowed = async (control_id) => {
    return await axiosWithAuth0.get(
      `limits/controls/${control_id}/is-allowed`,
      {}
    );
  };

  const checkIfPOC = async () => {
    return axiosWithAuth0.get(`limits/is-in-poc`, {});
  };

  const getMitreAttackTechniques = async () => {
    return axiosWithAuth0.get(`/posture/mitre/techniques`);
  };

  const getMitreHistory = async () => {
    return axiosWithAuth0.get(`/posture/mitre/history`);
  };

  const getPOCLimits = async () => {
    return axiosWithAuth0.get(`limits/poc`, {});
  };

  const setPOCLimits = async (
    customer_id,
    total_limit,
    daily_limit,
    reset_counts = false
  ) => {
    return axiosWithAuth0.post("support/poc-limits/set-limits", {
      customer_id,
      total_limit,
      daily_limit,
      reset_counts,
    });
  };

  const removePOCLimits = async (customer_id) => {
    return axiosWithAuth0.post("support/poc-limits/remove-limits", {
      customer_id,
    });
  };

  const getPOCStatus = async (customer_id) => {
    return axiosWithAuth0.post("support/poc-limits/get-status", {
      customer_id,
    });
  };

  const deleteJiraIssueByControlId = async (controlId) => {
    return axiosWithAuth0.delete(`tickets/jira/control/${controlId}`);
  };

  const deleteJiraIssueByVulnerabilityId = async (vulnerabilityId) => {
    return axiosWithAuth0.delete(
      `tickets/jira/vulnerability/${vulnerabilityId}`
    );
  };

  const deleteJiraIssuePerMisconfiguration = async (misconfigurationIds) => {
    return axiosWithAuth0.delete(`tickets/per-misconfiguration`, {
      data: {
        misconfiguration_ids: misconfigurationIds,
      },
    });
  };

  const deleteAsanaTask = async (controlId) => {
    return axiosWithAuth0.delete(`tickets/asana`, {
      params: { control_id: controlId },
    });
  };

  const createServiceNowIncident = async (incidentContent) => {
    return axiosWithAuth0.post(
      `integrations/servicenow/create-incident`,
      incidentContent
    );
  };

  const getServiceNowIncidentByControlID = async (controlId) => {
    return axiosWithAuth0.get(`tickets/servicenow`, {
      params: { control_id: controlId },
    });
  };

  const linkExistingServiceNowIncident = async (
    incidentLink,
    controlId,
    misconfigurationId = null
  ) => {
    return axiosWithAuth0.post(
      `integrations/servicenow/link-existing-incident`,
      {
        incident_link: incidentLink,
        control_id: controlId,
        misconfiguration_id: misconfigurationId,
      }
    );
  };

  const closeServiceNowIncident = async (controlId) => {
    return axiosWithAuth0.delete(`tickets/servicenow`, {
      params: { control_id: controlId },
    });
  };

  const getServiceNowCategories = async () => {
    return axiosWithAuth0.get(`integrations/servicenow/categories`);
  };

  const getServiceNowUrgencies = async () => {
    return axiosWithAuth0.get(`integrations/servicenow/urgencies`);
  };

  const fetchAllAdminUsers = async () => {
    return axiosWithAuth0.get(`/permissions/review/users`, {});
  };

  const createSSOOktaConnection = async (
    clientId,
    clientSecret,
    oktaDomain
  ) => {
    return axiosWithAuth0.post(`auth/create-okta-integration`, {
      client_id: clientId,
      client_secret: clientSecret,
      okta_domain: oktaDomain,
    });
  };

  const getAuth0CallbackUrl = async () => {
    return axiosWithAuth0.get(`auth/callback-url`, {});
  };

  /**
   * @deprecated This function is deprecated and should not be used in new code.
   */
  const isSsoOktaIntegrated = async () => {
    return axiosWithAuth0.get(`auth/is-sso-okta-integrated`, {});
  };

  const isSsoIntegrated = async () => {
    return axiosWithAuth0.get(`auth/is-sso-integrated`, {});
  };

  const generateSelfServiceSSOTicket = async () => {
    return axiosWithAuth0.get(`auth/self-service-sso/ticket`, {});
  };

  const getOktaInitiateLoginURI = async () => {
    return axiosWithAuth0.get(`auth/okta/initiate-login-uri`, {});
  };

  const getActionToRoleMapping = async () => {
    return axiosWithAuth0.get(`access/action-to-role`, {});
  };

  const getAllPlatformUsers = async () => {
    return axiosWithAuth0.get(`access/users`, {});
  };

  const getPlatformUser = async () => {
    return axiosWithAuth0.get(`access/user`, {});
  };

  const getUserEmail = async () => {
    return axiosWithAuth0.get(`access/user-email`, {});
  };

  const createPlatformUser = async (
    email,
    displayName,
    title,
    ownerships,
    isAdmin,
    accessRole
  ) => {
    return axiosWithAuth0.post(`access/users`, {
      email: email,
      display_name: displayName,
      role: title,
      ownerships: ownerships,
      is_admin: isAdmin,
      access_role: accessRole,
    });
  };

  const updatePlatformUser = async (
    email,
    displayName,
    title,
    ownerships,
    isAdmin,
    accessRole
  ) => {
    return axiosWithAuth0.post(`access/users/update`, {
      email: email,
      display_name: displayName,
      role: title,
      ownerships: ownerships,
      is_admin: isAdmin,
      access_role: accessRole,
    });
  };

  const updatePlatformUserDisplayInLocalTime = async (useOfLocalTime) => {
    return axiosWithAuth0.patch(
      `access/users/display-in-local-time`,
      useOfLocalTime
    );
  };

  const deletePlatformUser = async (userId) => {
    userId = doubleEncodeURI(userId);
    return axiosWithAuth0.delete(`access/users/${userId}`, {});
  };

  const updatePlatformUserOwnerships = async (userId, ownerships) => {
    userId = doubleEncodeURI(userId);
    return axiosWithAuth0.post(`access/users/${userId}/ownerships`, {
      ownerships: ownerships,
    });
  };

  const updatePlatformUserAccessRole = async (userId, access_role) => {
    userId = doubleEncodeURI(userId);
    return axiosWithAuth0.post(`access/users/${userId}/access-role`, {
      access_role: access_role,
    });
  };

  const updatePlatformUserTitle = async (userId, title) => {
    userId = doubleEncodeURI(userId);
    return axiosWithAuth0.post(`access/users/${userId}/title`, {
      role: title,
    });
  };

  const checkPlatformUserPermission = async (action, integration = null) => {
    return axiosWithAuth0.get(`access/user/check-user-permission`, {
      params: {
        action,
        integration,
      },
    });
  };

  const upgradeUserToMSSP = async (customerId, userEmail) => {
    return axiosWithAuth0.post(`support/mssp/upgrade-to-mssp`, {
      user_customer_id: customerId,
      user_email: userEmail,
    });
  };

  const addMSSPTenant = async (tenantData) => {
    return axiosWithAuth0.post(`mssp/tenants/add`, tenantData);
  };

  const editMSSPTenant = async (tenantData) => {
    return axiosWithAuth0.post(`mssp/tenants/edit`, tenantData);
  };

  const deleteMSSPTenant = async (tenantId) => {
    return axiosWithAuth0.delete(`mssp/tenants/${tenantId}`, {});
  };

  const getMSSPTenants = async () => {
    return axiosWithAuth0.get(`mssp/tenants`, {});
  };

  const setMSSPTenantIsPinned = async (tenantId, isPinned) => {
    return axiosWithAuth0.post(`mssp/tenants/${tenantId}/pin`, null, {
      params: {
        is_pinned: isPinned,
      },
    });
  };

  const setMSSPTenantLabels = async (tenantId, labels) => {
    return axiosWithAuth0.post(`mssp/tenants/${tenantId}/labels`, {
      labels: labels,
    });
  };

  const attachExistingCustomerToMSSP = async (customerId, msspId) => {
    return axiosWithAuth0.post(`support/mssp/tenants/attach-existing`, {
      customer_id: customerId,
      mssp_id: msspId,
    });
  };

  const copyTenantsToBreezeMSSP = async (sourceMsspId) => {
    return axiosWithAuth0.post(`support/mssp/tenants/copy-to-breeze`, {
      source_mssp_id: sourceMsspId,
    });
  };

  const getMSSPAssetsBreakdown = async () => {
    return axiosWithAuth0.get(`mssp/dashboard/assets-breakdown`, {});
  };

  const getMSSPTopIssues = async () => {
    return axiosWithAuth0.get(`mssp/dashboard/top-issues`, {});
  };

  const getMSSPStackUtilizationCoverage = async () => {
    return axiosWithAuth0.get(`mssp/dashboard/stack-utilization-widget`, {});
  };

  const getMSSPStackCoverageTable = async (tool) => {
    return axiosWithAuth0.get(
      `mssp/dashboard/stack-coverage-table/${tool}`,
      {}
    );
  };

  const sendScheduledNotification = async () => {
    return axiosWithAuth0.post(
      `support/notifications/scheduled-notification`,
      {}
    );
  };

  const getScheduledEmails = async () => {
    return axiosWithAuth0.get(`support/notifications/scheduled-emails`, {});
  };

  const getScheduledEmailsDetailed = async () => {
    return axiosWithAuth0.get(
      `support/notifications/scheduled-emails-detailed`,
      {}
    );
  };

  const updateScheduledEmailJob = async (customerId, jobId, updateData) => {
    return axiosWithAuth0.patch(
      `support/notifications/scheduled-emails/${customerId}/${jobId}`,
      updateData
    );
  };

  const deleteScheduledEmailJob = async (customerId, jobId) => {
    return axiosWithAuth0.delete(
      `support/notifications/scheduled-emails/${customerId}/${jobId}`
    );
  };

  const batchDeleteScheduledEmailJobs = async (emailJobs) => {
    return Promise.all(
      emailJobs.map((job) =>
        axiosWithAuth0.delete(
          `support/notifications/scheduled-emails/${job.customer_id}/${job.job_id}`
        )
      )
    );
  };

  const createScheduledEmailJob = async (emailData) => {
    return axiosWithAuth0.post(
      `support/notifications/scheduled-emails`,
      emailData
    );
  };

  const refreshEmailScheduler = async () => {
    return axiosWithAuth0.post(
      `support/notifications/refresh-email-scheduler`,
      {}
    );
  };

  const getScheduledSlackMessages = async () => {
    return axiosWithAuth0.get(
      `support/notifications/scheduled-slack-messages`,
      {}
    );
  };

  const removeSlackScheduledMessages = async (messages) => {
    return axiosWithAuth0.delete(
      `support/notifications/scheduled-slack-messages`,
      {
        data: messages,
      }
    );
  };

  const migrateData = async () => {
    return axiosWithAuth0.post(`migrate`, {});
  };

  const fetchNotificationSettings = async () => {
    return axiosWithAuth0.get(`notifications/settings`, {});
  };

  const fetchNotificationSetting = async (settingId) => {
    return axiosWithAuth0.get(`notifications/settings/${settingId}`, {});
  };

  const sendMicrosoftTeamsAdaptiveCardMessage = async (
    conversationId,
    adaptiveCardBody
  ) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_teams/send-adaptive-card-message`,
      {
        conversation_id: conversationId,
        adaptive_card_body: adaptiveCardBody,
      }
    );
  };

  const updateNotificationSetting = async (method, fieldID, newValue) => {
    return axiosWithAuth0.patch(
      `notifications/settings`,
      {
        method: method,
        field_id: fieldID,
        value: newValue,
      },
      {}
    );
  };

  const sendEmailGRC = async (formData, tool_email) => {
    return axiosWithAuth0.post(
      `permissions/review/send_email`,
      {
        recipient: formData.emails,
        subject: formData.subject,
        name: formData.userName,
        last_seen: formData.lastSeen,
        access_permissions: formData.accessPermissions,
        from_email: formData.emailFrom,
        given_name: formData.from,
        manager: formData.manager,
        tool_email: tool_email,
        tool_name: formData.toolName,
      },
      {}
    );
  };
  const approveUserPermissions = async (tool_email) => {
    return axiosWithAuth0.post(
      `permissions/review/approve_user_permissions`,
      {
        tool_email: tool_email,
      },
      {}
    );
  };
  const approveUserPermissionsBatch = async (usersToApprove) => {
    return axiosWithAuth0.post(
      `permissions/review/approve_user_permissions_batch`,
      {
        users_to_approve: usersToApprove,
      },
      {}
    );
  };

  const exportControlMisconfigurations = async (
    controlId,
    misconfigurationIds
  ) => {
    return axiosWithAuth0.get(
      `posture/controls/${controlId}/misconfigurations/export`,
      {
        params: {
          misconfiguration_ids: misconfigurationIds,
        },
        paramsSerializer,
      }
    );
  };

  const exportAuditLogs = async (userEmails, fromDate, toDate) => {
    return axiosWithAuth0.post(`audit-logs/events/export`, {
      user_emails: userEmails,
      from_date: fromDate,
      to_date: toDate,
    });
  };

  const getAllDetections = async () => {
    return axiosWithAuth0.get(`detections`, {});
  };

  const getSingleDetection = async (detectionId) => {
    return axiosWithAuth0.get(`detections/single/${detectionId}`, {});
  };

  const getDetectionByControlID = async (control_id) => {
    return axiosWithAuth0.get(`detections/control/${control_id}`, {});
  };

  const getDetectionByStepID = async (step_id) => {
    return axiosWithAuth0.get(`detections/step/${step_id}`, {});
  };

  const resolveDetection = async (detection_id, userName, email) => {
    return axiosWithAuth0.patch(`detections/resolve`, {
      detection_id: detection_id,
      user_name: userName,
      user_email: email,
    });
  };

  const getAllDetectedStepIDs = async () => {
    return axiosWithAuth0.get(`posture/steps/detected/all`, {});
  };

  const fetchAllInAppNotifications = async () => {
    return axiosWithAuth0.get(`inbox`, {});
  };

  const markInAppNotificationAsRead = async (notificationId) => {
    return axiosWithAuth0.patch(`inbox/mark-as-read/${notificationId}`, {});
  };

  const markAllInAppNotificationAsRead = async (notificationIds) => {
    return axiosWithAuth0.patch(`inbox/mark-all-as-read`, {
      notification_ids: notificationIds,
    });
  };

  const deleteInAppNotification = async (notificationId) => {
    return axiosWithAuth0.patch(`inbox/delete/${notificationId}`, {});
  };

  const deleteAllInAppNotifications = async () => {
    return axiosWithAuth0.patch(`inbox/delete-all`, {});
  };

  const createDummyInAppNotifications = async () => {
    return axiosWithAuth0.post(`support/notifications/inbox/create-dummy`, {});
  };

  const createAppManualInAppNotification = async (notification) => {
    return axiosWithAuth0.post(`support/notifications/inbox/create-manual`, {
      user_emails: notification.user_emails,
      title: notification.title,
      description: notification.description,
      link: notification.link,
      notification_type: notification.notification_type,
      icon_name: notification.icon_name,
      label: notification.label,
    });
  };

  const fetchAllUsersMail = async () => {
    return axiosWithAuth0.get(`support/notifications/inbox/users-email`, {});
  };

  const markInAppNotificationAsUnread = async (notificationId) => {
    return axiosWithAuth0.patch(`inbox/mark-as-unread/${notificationId}`, {});
  };

  const getRootExternalId = async () => {
    return axiosWithAuth0.get(`integrations/aws/root-external-id`);
  };

  const generateExternalApiKey = async (keyName, expiryInDays) => {
    return axiosWithAuth0.post(`api/v1/generate-api-key`, {
      key_name: keyName,
      expiry_days: expiryInDays,
    });
  };

  const getExternalApiKeys = async () => {
    return axiosWithAuth0.get(`api/v1/api-keys`, {});
  };

  const getBreezeBackendURL = async () => {
    return axiosWithAuth0.get(`api/v1/breeze-backend-url`, {});
  };

  const getAgentToken = async () => {
    return axiosWithAuth0.get(`agent/connector/token`, {});
  };

  const getBreezePlatformURL = async () => {
    return axiosWithAuth0.get(`api/v1/breeze-platform-url`, {});
  };

  const onboardNewCustomer = async (customerData) => {
    return axiosWithAuth0.post(`/support/onboarding/customer`, customerData);
  };

  const initiateNewEnv = async (customerData) => {
    return axiosWithAuth0.post(
      `/support/onboarding/initiate-env`,
      customerData
    );
  };

  const createUserWithNotifications = async (userData) => {
    return axiosWithAuth0.post(
      `support/onboarding/users/create-with-notifications`,
      userData
    );
  };

  const getCustomerList = async () => {
    return axiosWithAuth0.get(`support/onboarding/customers`, {});
  };

  const getCustomersWithSecretStatus = async () => {
    return axiosWithAuth0.get(`offboarding/customers-with-status`, {});
  };

  const getProtectedCustomers = async () => {
    return axiosWithAuth0.get(`offboarding/protected-customers`, {});
  };

  const offboardCustomer = async (
    customerId,
    options = {
      scheduleCustomerSecretsDeletion: true,
      scheduleSupportSecretsDeletion: true,
      removeFromPlatformConfig: true,
      updateJournalSecret: true,
      removeFromSupportLambdaSecret: true,
      deleteFromDynamoDB: false,
      deletePlatformUsers: false,
      deleteS3Bucket: false,
      deleteSupportS3Bucket: false,
    }
  ) => {
    return axiosWithAuth0.post(`offboarding/customer`, {
      customer_id_to_offboard: customerId,
      schedule_customer_secrets_deletion:
        options.scheduleCustomerSecretsDeletion,
      schedule_support_secrets_deletion: options.scheduleSupportSecretsDeletion,
      remove_from_platform_config: options.removeFromPlatformConfig,
      update_journal_secret: options.updateJournalSecret,
      remove_from_support_lambda_secret: options.removeFromSupportLambdaSecret,
      delete_from_dynamodb: options.deleteFromDynamoDB,
      delete_platform_users: options.deletePlatformUsers,
      delete_s3_bucket: options.deleteS3Bucket,
      delete_support_s3_bucket: options.deleteSupportS3Bucket,
    });
  };

  const deleteControl = async (customerId, controlId) => {
    return axiosWithAuth0.delete(`support/cleanup/delete-control`, {
      data: {
        customer_id: customerId,
        control_id: controlId,
      },
    });
  };

  const getCrowdstrikeCveFirstCollect = async () => {
    return axiosWithAuth0.get(
      `/environment/vulnerabilities/crowdstrike-first-collect`,
      {}
    );
  };

  const getHeatmapMatrixHistory = async () => {
    return axiosWithAuth0.get(`posture/heatmap/heatmap-matrix-history`, {});
  };

  const getPostureHeatmapCoverageTrend = async () => {
    return axiosWithAuth0.get(`custom-dashboard/posture-coverage-trend`, {});
  };

  const getPostureCoverage = async () => {
    return axiosWithAuth0.get(`custom-dashboard/posture-coverage`, {});
  };
  const getNISTSnapshot = async () => {
    return axiosWithAuth0.get(`nist/snapshot`, {});
  };

  const createAsanaTask = async (taskContent) => {
    return axiosWithAuth0.post(`integrations/asana/create-task`, taskContent);
  };

  const getAsanaProjects = async () => {
    return axiosWithAuth0.get(`integrations/asana/projects`);
  };

  const getAsanaUsers = async () => {
    return axiosWithAuth0.get(`integrations/asana/users`);
  };

  const linkExistingAsanaTask = async (
    ticketLink,
    controlId,
    misconfigurationId
  ) => {
    return axiosWithAuth0.post(`integrations/asana/link-existing-task`, {
      ticket_link: ticketLink,
      control_id: controlId,
      misconfiguration_id: misconfigurationId,
    });
  };

  const unlinkTicket = async (
    controlId = null,
    misconfigurationId = null,
    vulnerabilityId = null
  ) => {
    return axiosWithAuth0.post(`tickets/unlink`, {
      control_id: controlId,
      misconfiguration_id: misconfigurationId,
      vulnerability_id: vulnerabilityId,
    });
  };

  const getBucketNameFromCustomerDetails = async (accountId, customerId) => {
    return axiosWithAuth0.get(`support/platform-config/aws/s3/bucket-name`, {
      params: {
        account_id: accountId,
        customer_id: customerId,
      },
    });
  };

  const registerServiceNowIntegration = async (
    instanceUrl,
    username,
    password
  ) => {
    return axiosWithAuth0.post(`integrations/servicenow/register`, {
      instance_url: formatUrl(instanceUrl),
      username: username,
      password: password,
    });
  };

  const registerServiceNowCMDBIntegration = async (
    instanceUrl,
    username,
    password
  ) => {
    return axiosWithAuth0.post(`integrations/servicenow_cmdb/register`, {
      instance_url: formatUrl(instanceUrl),
      username: username,
      password: password,
    });
  };

  const fetchServiceNowCMDBCompanies = async () => {
    return axiosWithAuth0.get(`integrations/servicenow_cmdb/companies`);
  };

  const getServiceNowCMDBConfiguration = async () => {
    return axiosWithAuth0.get(`integrations/servicenow_cmdb/configuration`);
  };

  const updateServiceNowCMDBConfiguration = async (configData) => {
    return axiosWithAuth0.post(
      `integrations/servicenow_cmdb/configuration`,
      configData
    );
  };

  const revokeAtlassianUserAccess = async (controlId, userId, resourceIds) => {
    return axiosWithAuth0.post(`integrations/atlassian/user-access/suspend`, {
      control_id: controlId,
      user_id: userId,
      ids_to_revoke: resourceIds,
    });
  };

  const deleteAtlassianApiToken = async (controlId, apiTokens) => {
    return axiosWithAuth0.post(`integrations/atlassian/api-token/delete`, {
      control_id: controlId,
      api_tokens: apiTokens,
    });
  };
  const deleteAtlassianApiKey = async (controlId, apiKeys) => {
    return axiosWithAuth0.post(`integrations/atlassian/api-key/revoke`, {
      control_id: controlId,
      api_keys: apiKeys,
    });
  };

  const updateOwnAccessRole = async (access_role) => {
    return axiosWithAuth0.post(`access/user/self/access-role`, {
      access_role: access_role,
    });
  };

  const checkAWSEnvironmentWarning = async () => {
    return axiosWithAuth0.get(`access/aws-environment-warning`);
  };

  const registerZendeskIntegration = async (apiKey, email, url) => {
    return axiosWithAuth0.post(`integrations/zendesk/register`, {
      api_key: apiKey,
      email: email,
      url: url,
    });
  };

  const registerOneTrustIntegration = async (clientId, clientSecret, tenantUrl) => {
    return axiosWithAuth0.post(`integrations/onetrust/register`, {
      client_id: clientId,
      client_secret: clientSecret,
      tenant_url: tenantUrl,
    });
  };

  const getOnboardingStage = async () => {
    return axiosWithAuth0.get(`onboarding/stage`);
  };

  const updateOnboardingStage = async (stage) => {
    return axiosWithAuth0.post(`onboarding/update-stage`, { stage });
  };

  const saveMicrosoftTenantID = async (tenantID) => {
    return axiosWithAuth0.post(
      `integrations/microsoft_teams/tenant-id`,
      tenantID
    );
  };

  const sendMicrosoftTeamsTestMessage = async () => {
    return axiosWithAuth0.post(`integrations/microsoft_teams/test-message`, {});
  };

  const registerCloudConnexaOpenVPNIntegration = async (
    clientId,
    clientSecret,
    baseUrl
  ) => {
    return axiosWithAuth0.post(`integrations/cloudconnexa_open_vpn/register`, {
      client_id: clientId,
      client_secret: clientSecret,
      base_url: baseUrl,
    });
  };

  const isEntityExcludedInControls = async (entityId, controlIds) => {
    const doubleEncodedEntityId = doubleEncodeURI(entityId);
    return axiosWithAuth0.get(
      `environment/entities/${doubleEncodedEntityId}/exclusions`,
      {
        params: {
          control_ids: controlIds,
        },
        paramsSerializer,
      }
    );
  };

  const saveFilters = async (page, savedFilter) => {
    return axiosWithAuth0.post(`filters/save`, {
      page: page,
      saved_filter: savedFilter,
    });
  };

  const loadFilters = async (page) => {
    return axiosWithAuth0.get(`filters/load/${page}`);
  };

  const deleteSavedFilter = async (page, filterId) => {
    return axiosWithAuth0.delete(`filters/delete/${page}/${filterId}`);
  };

  const updateSavedFilter = async (page, filterId, updateData) => {
    return axiosWithAuth0.patch(
      `filters/update/${page}/${filterId}`,
      updateData
    );
  };

  const registerMicrosoftSCCMCloudIntegration = async (
    ad_username,
    ad_user_password,
    ad_domain_name,
    sccm_site_server_ip
  ) => {
    return axiosWithAuth0.post(`integrations/microsoft_sccm/register-cloud`, {
      ad_username: ad_username,
      ad_user_password: ad_user_password,
      ad_domain_name: ad_domain_name,
      sccm_site_server_ip: sccm_site_server_ip,
    });
  };

  const registerMicrosoftSCCMOnPremIntegration = async (
    ad_username,
    ad_user_password,
    ad_domain_name,
    sccm_site_server_ip
  ) => {
    return axiosWithAuth0.post(`integrations/microsoft_sccm/register-onprem`, {
      ad_username: ad_username,
      ad_user_password: ad_user_password,
      ad_domain_name: ad_domain_name,
      sccm_site_server_ip: sccm_site_server_ip,
    });
  };

  const registerSilverfortCloudIntegration = async (
    appUserId,
    appUserSecret,
    apiEndpoint
  ) => {
    return axiosWithAuth0.post(`integrations/silverfort/register-cloud`, {
      app_user_id: appUserId,
      app_user_secret: appUserSecret,
      api_endpoint: apiEndpoint,
    });
  };

  const registerSilverfortOnPremIntegration = async (
    appUserId,
    appUserSecret,
    apiEndpoint
  ) => {
    return axiosWithAuth0.post(`integrations/silverfort/register-onprem`, {
      app_user_id: appUserId,
      app_user_secret: appUserSecret,
      api_endpoint: apiEndpoint,
    });
  };

  const getEntityFields = async (entityType) => {
    return axiosWithAuth0.get(`postgres/entities/fields`, {
      params: { entity_type: entityType },
    });
  };

  const getEntityFieldsForExport = async (tenantIds) => {
    return axiosWithAuth0.get(`postgres/entities/fields/export`, {
      params: {
        tenant_ids: tenantIds?.length > 0 ? tenantIds.join(",") : undefined,
      },
    });
  };

  const getAgentConnectorImages = async () => {
    return axiosWithAuth0.get(`agent/connector/images`);
  };

  const getAgentConnectorConfigs = async () => {
    return axiosWithAuth0.get(`agent/connector/configs`);
  };

  const downloadAgentConnectorConfig = async (
    configName,
    version,
    fileType
  ) => {
    return axiosWithAuth0.get(`agent/connector/configs/download`, {
      params: {
        config_name: configName,
        version: version,
        file_type: fileType,
      },
      responseType: "blob",
    });
  };

  const downloadAgentConnectorImage = async (imageName, version, fileType) => {
    return axiosWithAuth0.get(`agent/connector/images/download`, {
      params: {
        image_name: imageName,
        version: version,
        file_type: fileType,
      },
      responseType: "blob",
    });
  };

  const uploadAgentConnectorImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosWithAuth0.post(
      `support/agent/connector/images/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  };

  const uploadAgentConnectorConfig = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosWithAuth0.post(
      `support/agent/connector/configs/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  };

  const refreshEntitiesCacheDB = async (customerId) => {
    return axiosWithAuth0.post(`support/cache/entities/refresh-db`, {
      customer_id: customerId,
    });
  };

  const refreshEntitiesCacheRouter = async (customerId, baseUrl) => {
    return axiosWithAuth0.post(`support/cache/entities/refresh-router`, {
      customer_id: customerId,
      base_url: baseUrl,
    });
  };

  const refreshPostgresEntitiesCacheDB = async (customerId) => {
    return axiosWithAuth0.post(`support/cache/postgres/refresh-db`, {
      customer_id: customerId,
    });
  };

  const createInventoryView = async (inventoryView) => {
    return axiosWithAuth0.post(`views/create-inventory-view`, inventoryView);
  };

  const getAllInventoryViews = async (multipleTenants = true) => {
    return axiosWithAuth0.get(`views/inventory-views`, {
      params: {
        multiple_tenants: multipleTenants,
      },
    });
  };

  const getAllCustomWidgets = async () => {
    return axiosWithAuth0.get(`custom-dashboard/custom-widgets`);
  };

  const createCustomWidget = async (customWidget) => {
    return axiosWithAuth0.post(`custom-dashboard/custom-widgets`, customWidget);
  };

  const updateInventoryView = async (inventoryView) => {
    return axiosWithAuth0.patch(`views/inventory-views`, inventoryView);
  };

  const getAuditLogs = async (userEmails = [], limit = 100, date = null) => {
    const params = {
      ...(userEmails && userEmails.length > 0
        ? { user_emails: userEmails }
        : {}),
      limit,
      ...(date ? { date: date } : {}),
    };
    return axiosWithAuth0.get(`/audit-logs/events`, {
      params,
      paramsSerializer,
    });
  };

  const getInventoryBootstrap = async () => {
    return axiosWithAuth0.get(`inventory/bootstrap`);
  };

  return {
    uploadAgentConnectorConfig,
    uploadAgentConnectorImage,
    downloadAgentConnectorConfig,
    getAgentConnectorConfigs,
    downloadAgentConnectorImage,
    getAgentConnectorImages,
    getEntityFields,
    getEntityFieldsForExport,
    registerAddigyIntegration,
    registerCatoIntegration,
    getIntegrationsInfo,
    getIntegrationsMCPIDs,
    setMCPIDToIntegration,
    testJamfMCPScriptSearchID,
    testIntuneMCPScriptSearchID,
    testKandjiMCPScriptSearchID,
    updateEntityExclusion,
    updateControlExclusions,
    excludeOrIncludeEntityInControls,
    fetchControlsBatch,
    registerJiraIntegration,
    registerMondayIntegration,
    registerAsanaIntegration,
    fetchIntegrations,
    removeIntegration,
    fetchGoogleAuthUrl,
    registerGcpIntegration,
    fetchGcpConfiguration,
    revokeGoogleTokens,
    checkGoogleRemediationPermissions,
    restrictGoogleFileAccess,
    removePersonalEmailPermissionsFromGoogleFiles,
    getAllAuditLogs,
    getRemediationAuditLogs,
    autoRemediate,
    reScanAfterAutoRemediation,
    rollbackAutoRemediation,
    resetRemediation,
    fetchSlackAuthUrl,
    fetchSlackSaaSAuthUrl,
    fetchSalesforceAuthUrl,
    fetchZoomAuthUrl,
    registerBoxIntegration,
    fetchSalesforceCallbackUrl,
    registerWizIntegration,
    fetchWizProjects,
    getWizConfiguration,
    updateWizConfiguration,
    registerSnykIntegration,
    registerOpenAIIntegration,
    registerCheckmarxIntegration,
    registerCrowdStrikeIntegration,
    checkCrowdStrikeReadScopes,
    getPlatformStaticIPs,
    getPlatformUrl,
    registerCarbonBlackIntegration,
    registerQualysIntegration,
    fetchQualysTags,
    getQualysConfiguration,
    updateQualysConfiguration,
    registerBambooHRIntegration,
    registerJamfIntegration,
    getJamfRolePermissions,
    checkJamfRolePermissions,
    registerJamfProtectIntegration,
    registerRapid7InsightVMIntegration,
    registerIntuneIntegration,
    registerMicrosoftIntegration,
    getRegisteredMicrosoftIntegrations,
    registerMicrosoftDefenderEndpointIntegration,
    getMicrosoftDefenderEndpointPermissions,
    registerMicrosoftOffice365Integration,
    getMicrosoftOffice365Permissions,
    getMicrosoftOffice365ExistingKMSkey,
    generateMicrosoft365KMSkey,
    getIntunePermissions,
    registerOktaIntegration,
    checkOktaReadScopes,
    checkAddigyPermissions,
    getOktaReadScopes,
    getOktaManagementScopes,
    checkOktaManagementScopes,
    disablePasswordRevealInOkta,
    registerTenableIntegration,
    registerAWSIntegration,
    registerAWSMembersAccounts,
    checkAWSRootAccountIsIntegrated,
    checkAWSMemberAccountAreIntegrated,
    updateAWSAccount,
    fetchAllAWSAccounts,
    getAWSUserPermissions,
    registerCloudflareIntegration,
    registerImpervaWAFIntegration,
    registerDigIntegration,
    registerSentraIntegration,
    registerOxIntegration,
    registerBitwardenIntegration,
    registerZscalerZiaIntegration,
    registerZscalerZpaIntegration,
    registerZscalerZccIntegration,
    registerTwingateIntegration,
    registerMongoAtlasIntegration,
    registerNetskopeIntegration,
    registerHiBobIntegration,
    registerGreenhouseIntegration,
    registerJumpCloudIntegration,
    registerNinjaOneIntegration,
    registerLayerXIntegration,
    getHiBobPermissions,
    getHiBobLifeCycleStatus,
    fetchProjects,
    fetchJiraIssues,
    fetchJiraIssueTypes,
    fetchJSMRequestTypes,
    fetchJiraIssueCustomFields,
    fetchJSMRequestFields,
    fetchJSMReporterOptions,
    checkJSMIsAgent,
    fetchJiraUsers,
    fetchJiraLabels,
    createJiraIssue,
    createJiraIssuePerMisconfiguration,
    createJSMIncident,
    createJSMIncidentPerMisconfiguration,
    editJiraIssue,
    fetchJiraIssueConfig,
    updateJiraIssueConfig,
    deleteJiraIssueConfig,
    getSlackChannelInfo,
    sendSlackChannelTestMessage,
    removeSlackChannel,
    fetchAWSAccountID,
    generateAWSExternalId,
    requestFullScan,
    requestPartialScan,
    requestPartialScanByTools,
    requestPostureScan,
    createHeatmap,
    getScanStatus,
    getIsScanning,
    getLastSuccessfulScan,
    getScanHealthChecks,
    runNightlyFlow,
    runNightlyFlowUI,
    fetchFocusedScenarios,
    fetchStepsByScenarioSet,
    fetchScenario,
    fetchControl,
    fetchControlMisconfigurations,
    fetchAllControls,
    fetchStep,
    fetchStepsBatch,
    fetchAllSteps,
    downloadPlaybook,
    fetchRegularAssets,
    getAssetsByName,
    getEntitiesByAssetID,
    createAsset,
    acceptSuggestion,
    generateAutoSuggestions,
    updateAsset,
    deleteAsset,
    updateAssetsContainingTheEntity,
    removeEntityFromAsset,
    snoozeControl,
    unsnoozeControl,
    snoozeMisconfiguration,
    unsnoozeMisconfiguration,
    assignControlAndRemediation,
    isPlaybookAvailable,
    deleteAWSAccount,
    trackEvent,
    fetchEntityById,
    fetchEntitiesByTypeRds,
    getEntitiesRds,
    fetchRawDataFields,
    fetchEntityRawDataRds,
    fetchEntityByIdRds,
    fetchAllEntities,
    fetchAllEntitiesRds,
    fetchEntitiesCount,
    fetchEntitiesCountRds,
    fetchEntitiesByType,
    getAllComplexMisconfiguredEntitiesForControl,
    getTerminatedUsersAndHosts,
    runEntityCollection,
    getEntitiesOverview,
    refreshEntitiesOverview,
    runVulnerabilityCollection,
    clearEntitiesCache,
    clearSecretsCache,
    clearRunningTasks,
    registerGitHubIntegration,
    registerGitLabIntegration,
    getUrlGithubInstall,
    registerMicrosoftEntraIDIntegration,
    getMicrosoftEntraIDPermissions,
    registerMicrosoftAzureCloudIntegration,
    getMicrosoftAzureCloudPermissions,
    getMicrosoftAzureCloudSubscriptions,
    registerOrcaIntegration,
    registerPerceptionPointIntegration,
    fetchNotionAuthUrl,
    registerTorqIntegration,
    registerWorkatoIntegration,
    registerCoralogixIntegration,
    registerSumoLogicIntegration,
    registerSplunkCloudIntegration,
    getTorqConfig,
    updateTorqConfig,
    getSplunkCloudConfig,
    updateSplunkCloudConfig,
    getPaloAltoCortexXSIAMConfig,
    updatePaloAltoCortexXSIAMConfig,
    getCoralogixConfig,
    updateCoralogixConfig,
    getSumoLogicConfig,
    updateSumoLogicConfig,
    getWorkatoConfig,
    updateWorkatoConfig,
    toggleFavoriteControl,
    getFavoriteControls,
    toggleFavoriteEntity,
    getFavoriteEntities,
    fetchAllRemediations,
    updateRemediationCost,
    fetchEnvFeatureFlags,
    updateEnvFeatureFlags,
    addEnvFeatureFlag,
    deleteEnvFeatureFlag,
    fetchPlatformSecret,
    updatePlatformSecretField,
    fetchRemediation,
    markControlAsViewed,
    checkControlIsViewed,
    getControlViewedToday,
    getRemainingControlsLimit,
    checkIfPOC,
    checkControlIsAllowed,
    getRemainingControlsLimitToday,
    getPOCLimits,
    setPOCLimits,
    removePOCLimits,
    getPOCStatus,
    refreshLimitsCache,
    getCreationAssetSuggestions,
    getUpdateAssetSuggestions,
    registerSnowflakeIntegration,
    generateSnowflakeKMSkey,
    getExistingSnowflakeKMSkey,
    registerKandjiIntegration,
    fetchEntitiesBatch,
    fetchRemediationsBatch,
    fetchVulnerabilities,
    fetchVulnerabilitiesBatch,
    exportVulnerabilitiesAssociations,
    fetchVulnerabilitiesByEntityId,
    getVulnerabilityFields,
    getMitreAttackTechniques,
    getMitreHistory,
    deleteJiraIssueByControlId,
    deleteJiraIssueByVulnerabilityId,
    deleteJiraIssuePerMisconfiguration,
    deleteAsanaTask,
    createSSOOktaConnection,
    getAuth0CallbackUrl,
    isSsoOktaIntegrated,
    isSsoIntegrated,
    generateSelfServiceSSOTicket,
    getOktaInitiateLoginURI,
    fetchNotificationSettings,
    fetchNotificationSetting,
    sendMicrosoftTeamsAdaptiveCardMessage,
    updateNotificationSetting,
    sendScheduledNotification,
    getScheduledEmails,
    getScheduledEmailsDetailed,
    updateScheduledEmailJob,
    deleteScheduledEmailJob,
    batchDeleteScheduledEmailJobs,
    createScheduledEmailJob,
    refreshEmailScheduler,
    getScheduledSlackMessages,
    removeSlackScheduledMessages,
    linkExistingJiraIssue,
    fetchJiraIsJSM,
    enableJiraJSM,
    disableJiraJSM,
    fetchAllAdminUsers,
    getActionToRoleMapping,
    getAllPlatformUsers,
    getPlatformUser,
    createPlatformUser,
    updatePlatformUser,
    updatePlatformUserDisplayInLocalTime,
    deletePlatformUser,
    updatePlatformUserOwnerships,
    updatePlatformUserAccessRole,
    updatePlatformUserTitle,
    checkPlatformUserPermission,
    upgradeUserToMSSP,
    addMSSPTenant,
    editMSSPTenant,
    deleteMSSPTenant,
    getMSSPTenants,
    setMSSPTenantIsPinned,
    setMSSPTenantLabels,
    attachExistingCustomerToMSSP,
    copyTenantsToBreezeMSSP,
    setScenarioSetModalWasOpened,
    fetchAllScenarioSets,
    registerPaloAltoCortexXDRIntegration,
    registerPaloAltoCortexXSIAMIntegration,
    uploadPaloAltoNGFWFile,
    registerPaloAltoNGFWIntegration,
    startMicrosoftActiveDirectoryFileUpload,
    completeMicrosoftActiveDirectoryFileUpload,
    registerMicrosoftActiveDirectoryOfflineIntegration,
    registerMicrosoftActiveDirectoryOnPremIntegration,
    getMicrosoftActiveDirectoryStatus,
    registerAkamaiWAFIntegration,
    registerAtlassianIntegration,
    registerCyberArkIdentityIntegration,
    sendEmailGRC,
    approveUserPermissions,
    runBuildGRCUser,
    fetchJiraDefaultIssueConfig,
    updateJiraIssueDefaultConfig,
    deleteJiraDefaultIssueConfig,
    getEntitiesByControlAndMisconfig,
    exportControlMisconfigurations,
    registerSecurityScorecardIntegration,
    getCloudFormationURL,
    getCloudFormationFolder,
    migrateData,
    updateSmartAsset,
    fetchSmartAssets,
    fetchAllAssets,
    generateSmartAsset,
    getCustomerFeatureFlagOptions,
    getDetectionOptions,
    getControlOptions,
    getAllCustomersFeatureFlags,
    fetchCustomerFeatureFlags,
    updateAssetReason,
    getSingleDetection,
    getAllDetections,
    getDetectionByControlID,
    getDetectionByStepID,
    resolveDetection,
    updateCustomerFeatureFlags,
    addCustomerFeatureFlag,
    updateCurrentCustomerFeatureFlag,
    addCustomerFeatureFlagsForAllCustomers,
    deleteCustomerFeatureFlag,
    getAllDetectedStepIDs,
    getTicketByControlID,
    getJiraIssueByControlID,
    getTicketByVulnerabilityId,
    getJiraIssueByVulnerabilityId,
    getTicketByMisconfigurationId,
    getAllTickets,
    getMisconfigurationsWithTickets,
    exportAuditLogs,
    fetchAllInAppNotifications,
    createDummyInAppNotifications,
    markInAppNotificationAsRead,
    markAllInAppNotificationAsRead,
    deleteInAppNotification,
    deleteAllInAppNotifications,
    markInAppNotificationAsUnread,
    createAppManualInAppNotification,
    fetchAllUsersMail,
    registerPaloAltoPrismaAccessBrowserIntegration,
    getRootExternalId,
    generateExternalApiKey,
    getExternalApiKeys,
    getBreezeBackendURL,
    getAgentToken,
    getBreezePlatformURL,
    onboardNewCustomer,
    initiateNewEnv,
    registerTalonIntegration,
    registerPanoraysIntegration,
    fetchSingleScenariosByScenarioSet,
    fetchVulnerabilitiesByEntityType,
    registerSentinelOneIntegration,
    registerAxurIntegration,
    registerCircleCIIntegration,
    registerClaudeIntegration,
    registerCursorIntegration,
    registerCheckpointHarmonyBrowseIntegration,
    registerCheckpointHarmonyEndpointIntegration,
    approveUserPermissionsBatch,
    createUserWithNotifications,
    getCustomerList,
    getCustomersWithSecretStatus,
    getProtectedCustomers,
    offboardCustomer,
    deleteControl,
    getOnboardingStage,
    updateOnboardingStage,
    getHeatmapMatrixHistory,
    fetchAssociationsByEntityType,
    getCrowdstrikeCveFirstCollect,
    getMSSPAssetsBreakdown,
    getMSSPTopIssues,
    getMSSPStackUtilizationCoverage,
    createAsanaTask,
    getAsanaProjects,
    getAsanaUsers,
    linkExistingAsanaTask,
    unlinkTicket,
    getMSSPStackCoverageTable,
    updateRemediationCustomPriority,
    updateControlCustomSeverity,
    getControlActivityLogs,
    addCommentToControlActivityLogs,
    editCommentContent,
    updateCommentResolveStatus,
    deleteComment,
    getBucketNameFromCustomerDetails,
    registerServiceNowIntegration,
    registerServiceNowCMDBIntegration,
    fetchServiceNowCMDBCompanies,
    getServiceNowCMDBConfiguration,
    updateServiceNowCMDBConfiguration,
    revokeAtlassianUserAccess,
    registerStarburstIntegration,
    createServiceNowIncident,
    getServiceNowIncidentByControlID,
    linkExistingServiceNowIncident,
    closeServiceNowIncident,
    getServiceNowCategories,
    getServiceNowUrgencies,
    getNISTSnapshot,
    updateOwnAccessRole,
    checkAWSEnvironmentWarning,
    registerZendeskIntegration,
    registerOneTrustIntegration,
    saveDashboard,
    loadDashboard,
    saveAIDashboard,
    loadAIDashboard,
    getNHITypesDistributionTrend,
    getTopMCPServersTrend,
    getAIAppUsageTrend,
    getToolsPerformanceTrend,
    saveMicrosoftTenantID,
    sendMicrosoftTeamsTestMessage,
    getCriticalAssetsTrend,
    getUserEmail,
    getInventoryAssetsTrend,
    registerCloudConnexaOpenVPNIntegration,
    createLabel,
    getAllLabels,
    updateEntitiesLabelsBatch,
    deleteLabel,
    createTag,
    getAllCustomTags,
    updateEntitiesTagsBatch,
    deleteTag,
    getToolsCoverageTrend,
    deleteAtlassianApiToken,
    isEntityExcludedInControls,
    saveFilters,
    loadFilters,
    deleteSavedFilter,
    updateSavedFilter,
    deleteAtlassianApiKey,
    getActivityLogImproved,
    getToolsRemediationPerformanceTrend,
    getTeamRemediationPerformanceTrend,
    getPostureHeatmapCoverageTrend,
    getPostureCoverage,
    registerMicrosoftSCCMCloudIntegration,
    registerMicrosoftSCCMOnPremIntegration,
    registerSilverfortCloudIntegration,
    registerSilverfortOnPremIntegration,
    getOpenGapsAgeTrend,
    refreshEntitiesCacheDB,
    refreshEntitiesCacheRouter,
    refreshPostgresEntitiesCacheDB,
    createInventoryView,
    getAllInventoryViews,
    updateInventoryView,
    getAllCustomWidgets,
    createCustomWidget,
    getAuditLogs,
    fetchEntitiesByVulnerability,
    fetchEntityInstalledPrograms,
    getInventoryBootstrap,
    fetchProgramFilterOptions,
    exportEntities,
    fetchEntityFilterOptions,
    generateInternalDashboardSnapshots,
    getInternalDashboard,
    getInternalDashboardTrends,
    saveInternalDashboardLayout,
    loadInternalDashboardLayout,
    sendErrorReport,
  };
};

export default useAPI;
